// API client for backend communication
import { localStore } from './local-store';
import type { ReconciledInfo } from '@/components/reports/stocktake-aggregation';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const REQUEST_TIMEOUT_MS = 15000; // 15 seconds
// A whole-catalog product upload (3,000+ rows) is validated and written in
// one request; it must not be cut off at the normal 15 s.
const IMPORT_TIMEOUT_MS = 5 * 60 * 1000;

/** One "Cancel items" event on a staged pick list (sales-order page). */
export type SalesOrderTakeOff = {
  id: string;
  outboundOrderId: string;
  orderNumber: string;
  takenAt: string;
  takenBy: { name: string | null } | null;
  unitCount: number;
  note: string | null;
  putbackHeaderId: string | null;
  putbackEpisodeNo: number | null;
  /** "OUT-… #2" — the Putback episode as the Putback screens name it. */
  putbackRef: string | null;
  /** Units Putback has already scanned onto a shelf. */
  returnedUnits: number;
  products: Array<{ productId: string; catalogNumber: string | null; productName: string | null; count: number }>;
  units: Array<{
    inventoryItemId: string;
    productId: string;
    catalogNumber: string | null;
    productName: string | null;
    epc: string | null;
    serialNumber: string | null;
    containerLpn: string | null;
    batchNo: string | null;
    expiryDate: string | null;
    putbackStatus: string | null;
    putbackLocationName: string | null;
  }>;
};

export class ApiError extends Error {
  status: number;
  /** The parsed error body, so a caller can act on a machine-readable `code`. */
  data?: any;
  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Eagerly restore token from localStorage to avoid race with auth context useEffect
    if (typeof window !== 'undefined') {
      this.authToken = localStorage.getItem('auth_token');
    }
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private isNetworkError(error: unknown): boolean {
    return error instanceof TypeError &&
      (error.message.includes('fetch') ||
       error.message.includes('Failed to fetch') ||
       error.message.includes('NetworkError') ||
       error.message.includes('Network request failed') ||
       error.message.includes('Load failed'));
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0,
    timeoutMs: number = REQUEST_TIMEOUT_MS,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>,
    };

    // Only set Content-Type for requests with a string body (JSON).
    // FormData bodies need the browser to auto-set the multipart boundary.
    if (typeof options.body === 'string' || (!options.body && options.method !== 'GET')) {
      headers['Content-Type'] = 'application/json';
    }

    // Add auth token if available
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      if (!response.ok) {
        // Handle 401 Unauthorized (token expired/invalid)
        if (response.status === 401) {
          this.authToken = null;
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          window.dispatchEvent(new Event('auth:unauthorized'));
        }

        // 403 Forbidden = permission denied (don't logout, just throw error)

        let errorData;
        try {
          const text = await response.text();
          if (text) {
            errorData = JSON.parse(text);
          } else {
            errorData = {
              message: `HTTP error! status: ${response.status}`,
              status: response.status,
              statusText: response.statusText,
            };
          }
        } catch {
          errorData = {
            message: `HTTP error! status: ${response.status}`,
            status: response.status,
            statusText: response.statusText,
          };
        }
        
        // Handle nested NestJS error format: { message: { message: "...", error: "...", statusCode: 409 } }
        let errorMessage = errorData.message;
        if (typeof errorMessage === 'object' && errorMessage !== null) {
          errorMessage = errorMessage.message || errorMessage.error || JSON.stringify(errorMessage);
        }
        errorMessage = errorMessage || errorData.error;
        if (!errorMessage) {
          if (response.status === 404) {
            errorMessage = `Route not found: ${endpoint}. Make sure the backend is running and the route exists.`;
          } else {
            errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          }
        }
        throw new ApiError(errorMessage, response.status, errorData);
      }

      // Handle empty responses
      const text = await response.text();
      if (!text) {
        return null as T;
      }

      return JSON.parse(text);
    } catch (error) {
      // Treat timeout aborts as network errors
      const isTimeout = error instanceof DOMException && error.name === 'AbortError';
      const isNetwork = this.isNetworkError(error) || isTimeout;

      // Retry on network errors for idempotent methods only (GET/HEAD/OPTIONS)
      const method = (options.method || 'GET').toUpperCase();
      const isIdempotent = ['GET', 'HEAD', 'OPTIONS'].includes(method);
      if (isNetwork && isIdempotent && retryCount < MAX_RETRIES) {
        const retryDelay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
        await this.delay(retryDelay);
        return this.request<T>(endpoint, options, retryCount + 1, timeoutMs);
      }

      // Final failure after retries or non-network error
      if (isTimeout) {
        throw new Error(
          `Request timed out after ${timeoutMs / 1000}s. ` +
          `Please check your network connection and try again.`
        );
      }
      if (this.isNetworkError(error)) {
        throw new Error(
          `Failed to connect to backend after ${retryCount} retries. ` +
          `Please check your network connection and try again.`
        );
      }

      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async requestBlob(endpoint: string): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch { /* use default */ }
      throw new ApiError(errorMessage, response.status);
    }

    return response.blob();
  }

  // Generic HTTP methods for flexibility
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // Products (server-side pagination)
  async getProductsPaginated(options: {
    limit: number;
    offset: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** Show the deleted rows instead of the live ones, so one can be restored. */
    deleted?: boolean;
    /** One category, or UNCLASSIFIED_CATEGORY ("__none__") for products without one. */
    category?: string;
  }): Promise<{ items: any[]; total: number }> {
    const params = new URLSearchParams();
    params.append('limit', options.limit.toString());
    params.append('offset', options.offset.toString());
    if (options.search) params.append('search', options.search);
    if (options.category) params.append('category', options.category);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.deleted) params.append('deleted', 'true');
    return this.request<{ items: any[]; total: number }>(`/products?${params.toString()}`);
  }

  async searchProducts(query: string, limit: number = 20): Promise<any[]> {
    if (!query || query.trim().length === 0) return [];
    return this.request<any[]>(`/products/search?q=${encodeURIComponent(query.trim())}&limit=${limit}`);
  }

  async createProduct(data: any) {
    return this.request<any>('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProduct(id: string, _organizationId: string, data: any) {
    return this.request<any>(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProduct(id: string, _organizationId?: string) {
    return this.request<void>(`/products/${id}`, {
      method: 'DELETE',
    });
  }

  // The other way out: let a deleted product with nothing attached give up the
  // catalog number it is holding, so a typo can be corrected.
  async releaseCatalogNumber(id: string) {
    return this.request<{ previousCatalogNumber: string; catalogNumber: string }>(
      `/products/${id}/release-catalog-number`,
      { method: 'POST' },
    );
  }

  // A deleted product keeps its catalog number, so restoring it is the only way
  // to get that number back without inventing a new one.
  async restoreProduct(id: string) {
    return this.request<any>(`/products/${id}/restore`, {
      method: 'POST',
    });
  }

  // Product Attribute Definitions
  async getProductAttributeDefinitions() {
    return this.request<import('@/types/inventory').ProductAttributeDefinition[]>(
      '/product-attribute-definitions'
    );
  }

  async createProductAttributeDefinition(data: {
    name: string;
    key: string;
    fieldType: 'TEXT' | 'NUMBER' | 'SELECT';
    isRequired?: boolean;
    sortOrder?: number;
    options?: string[];
    defaultValue?: string | null;
  }) {
    return this.request<import('@/types/inventory').ProductAttributeDefinition>(
      '/product-attribute-definitions',
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  async updateProductAttributeDefinition(id: string, data: {
    name?: string;
    fieldType?: 'TEXT' | 'NUMBER' | 'SELECT';
    isRequired?: boolean;
    isActive?: boolean;
    sortOrder?: number;
    options?: string[];
    defaultValue?: string | null;
  }) {
    return this.request<import('@/types/inventory').ProductAttributeDefinition>(
      `/product-attribute-definitions/${id}`,
      { method: 'PUT', body: JSON.stringify(data) }
    );
  }

  async deleteProductAttributeDefinition(id: string) {
    return this.request<void>(
      `/product-attribute-definitions/${id}`,
      { method: 'DELETE' }
    );
  }

  // Attribute Suggestions
  async getAttributeSuggestions(query?: string, limit?: number) {
    const params = new URLSearchParams();
    if (query) params.append('query', query);
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    return this.request<Array<{ id: number; attributeName: string; usageCount: number }>>(
      `/products/attributes/suggestions${queryString ? `?${queryString}` : ''}`
    );
  }

  async getAttributeValueSuggestions(attributeName: string, query?: string, limit?: number) {
    const params = new URLSearchParams();
    if (query) params.append('query', query);
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    return this.request<Array<{ id: number; value: string; usageCount: number }>>(
      `/products/attributes/${encodeURIComponent(attributeName)}/values/suggestions${queryString ? `?${queryString}` : ''}`
    );
  }

  // Product Import
  async getProductImportFields() {
    return this.request<ProductImportFieldConfig[]>('/products/import/fields');
  }

  async parseProductImportFile(file: File): Promise<ProductImportParseResult> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}/products/import/parse`;
    const headers: Record<string, string> = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `Upload failed: ${response.statusText}`);
    }

    return response.json();
  }

  async validateProductImport(data: {
    fileId: string;
    columnMappings: Record<string, string>;
    defaultValues?: Record<string, any>;
    valueTransformations?: Record<string, Record<string, string>>;
  }) {
    return this.request<ProductImportValidationResult>('/products/import/validate', {
      method: 'POST',
      body: JSON.stringify(data),
    }, 0, IMPORT_TIMEOUT_MS);
  }

  async executeProductImport(data: {
    fileId: string;
    columnMappings: Record<string, string>;
    defaultValues?: Record<string, any>;
    valueTransformations?: Record<string, Record<string, string>>;
  }) {
    return this.request<ProductImportResult>('/products/import/execute', {
      method: 'POST',
      body: JSON.stringify(data),
    }, 0, IMPORT_TIMEOUT_MS);
  }

  // The product list as an .xlsx in the Bulk Upload's own columns, filtered
  // like the list page (search, category).
  async downloadProductsXlsx(filters: { search?: string; category?: string } = {}) {
    const params = new URLSearchParams();
    if (filters.search?.trim()) params.set('search', filters.search.trim());
    if (filters.category) params.set('category', filters.category);
    const qs = params.toString();
    return this.requestBlob(`/products/export.xlsx${qs ? `?${qs}` : ''}`);
  }

  // The blank Bulk Upload template (.xlsx with the Category drop-down).
  async downloadProductImportTemplate() {
    return this.requestBlob('/products/import/template.xlsx');
  }

  // NFC name cards — a card is a name stamp, not a login
  async getNfcCards() {
    return this.request<NfcCard[]>('/nfc-cards');
  }

  async createNfcCard(displayName: string) {
    return this.request<NfcCard>('/nfc-cards', {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    });
  }

  async updateNfcCard(id: string, data: { displayName?: string; isActive?: boolean }) {
    return this.request<NfcCard>(`/nfc-cards/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNfcCard(id: string) {
    return this.request<{ success: boolean }>(`/nfc-cards/${id}`, {
      method: 'DELETE',
    });
  }

  async unpairNfcCard(id: string) {
    return this.request<NfcCard>(`/nfc-cards/${id}/unpair`, { method: 'POST' });
  }

  // Product Import Templates
  async getProductImportTemplates() {
    return this.request<ProductImportTemplate[]>('/products/import/templates');
  }

  async createProductImportTemplate(data: {
    name: string;
    description?: string;
    columnMappings: Record<string, string>;
    defaultValues?: Record<string, any>;
    valueTransformations?: Record<string, Record<string, string>>;
    isDefault?: boolean;
  }) {
    return this.request<ProductImportTemplate>('/products/import/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Inbound Management
  async getInboundOrders(options?: {
    inboundType?: 'PURCHASE' | 'PRODUCTION' | 'DIRECT_RECEIPT' | 'RETURN';
    excludeInboundType?: 'PURCHASE' | 'PRODUCTION' | 'DIRECT_RECEIPT' | 'RETURN';
    warehouseId?: string;
    statuses?: string[];
    search?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const params = new URLSearchParams();
    if (options?.inboundType) params.set('inboundType', options.inboundType);
    if (options?.excludeInboundType) params.set('excludeInboundType', options.excludeInboundType);
    if (options?.warehouseId) params.set('warehouseId', options.warehouseId);
    if (options?.statuses?.length) params.set('statuses', options.statuses.join(','));
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset != null) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      items: any[];
      total: number;
      limit: number;
      offset: number;
    }>(`/inbound-orders${query}`);
  }

  async getInboundOrder(id: string) {
    return this.request<any>(`/inbound-orders/${id}`);
  }

  async getInboundOrderItems(id: string) {
    return this.request<Array<{
      id: string;
      productId: string;
      product: { id: string; catalogNumber: string; name: string };
      uom: string;
      quantityExpected: number;
      quantityReceived: number;
      quantityPrinted: number;
      quantityPutaway: number;
      batchNo?: string;
      expiryDate?: string;
      originalInventoryItemId?: string | null;
      skipPrint: boolean;
      printChoice?: 'ITEMS_ONLY' | 'BOX_ONLY' | 'BOX_AND_ITEMS';
      boxLabelPrintedAt?: string | null;
      plannedContainerKey?: string | null;
    }>>(`/inbound-orders/${id}/items`);
  }

  async getInboundInventoryItems(id: string) {
    return this.request<Array<{
      id: string;
      inboundItemId: string;
      epc: string | null;
      serialNumber: string | null;
      catalogNumber: string | null;
      productName: string | null;
      batchNo: string | null;
      expiryDate: string | null;
      putawayDate: string | null;
      putawayBy: string | null;
      /** LPN of the box the item sits in (box-mode inbound); location is then the box's. */
      boxLpn: string | null;
      /** The box itself (null for loose items). */
      box: {
        lpn: string;
        state: string | null;
        epc: string | null;
        labelPrintedAt: string | null;
        location: {
          zone: string | null;
          aisle: string | null;
          rack: string | null;
          level: string | null;
          bin: string | null;
        } | null;
      } | null;
      location: {
        zone: string | null;
        aisle: string | null;
        rack: string | null;
        level: string | null;
        bin: string | null;
      } | null;
    }>>(`/inbound-orders/${id}/inventory-items`);
  }

  async getReturnableItems(outboundOrderId: string) {
    return this.request<Array<{
      productId: string;
      productName: string;
      catalogNumber: string;
      uom: string;
      quantityOrdered: number;
      quantityReturned: number;
      returnableQuantity: number;
    }>>(`/inbound-orders/returnable-items/${outboundOrderId}`);
  }

  async getReturnableInventoryItems(outboundOrderId: string, excludeOrderId?: string) {
    const query = excludeOrderId ? `?excludeOrderId=${encodeURIComponent(excludeOrderId)}` : '';
    return this.request<Array<{
      productId: string;
      catalogNumber: string;
      productName: string;
      totalItemCount: number;
      batches: Array<{
        batchId: string | null;
        batchNo: string | null;
        expiryDate: string | null;
        itemCount: number;
        items: Array<{
          id: string;
          serialNumber: string | null;
          epc: string;
          status: string;
        }>;
      }>;
    }>>(`/inbound-orders/returnable-inventory/${outboundOrderId}${query}`);
  }

  async createInboundOrder(data: {
    inboundType?: string;
    referenceNumber?: string;
    supplierId?: string;
    reasonCodeId?: string;
    outboundOrderId?: string;
    warehouseId?: string;
    expectedArrivalDate?: string;
    items: Array<{
      productId: string;
      uom: string;
      quantityExpected: number;
      batchNo?: string;
      expiryDate?: string;
      originalInventoryItemId?: string;
      skipPrint?: boolean;
    }>;
  }) {
    return this.request<{ id: string; orderNumber: string }>('/inbound-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async addInboundItem(inboundOrderId: string, data: {
    productId: string;
    uom: string;
    quantityExpected: number;
    batchNo?: string;
    expiryDate?: string;
    originalInventoryItemId?: string;
    skipPrint?: boolean;
  }) {
    return this.request<{ id: string }>(`/inbound-orders/${inboundOrderId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInboundItem(inboundOrderId: string, itemId: string, data: {
    quantityExpected?: number;
    batchNo?: string | null;
    expiryDate?: string | null;
    skipPrint?: boolean;
  }) {
    return this.request<{ id: string }>(`/inbound-orders/${inboundOrderId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteInboundItem(inboundOrderId: string, itemId: string) {
    return this.request<any>(`/inbound-orders/${inboundOrderId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async validateInboundOrder(id: string, batchData?: {
    batchNumber?: string;
    expiryDate?: string;
    items?: Array<{
      itemId: string;
      batchNumber?: string;
      expiryDate?: string;
    }>;
  }) {
    return this.request<{ order: any; itemsGenerated: number }>(`/inbound-orders/${id}/validate`, {
      method: 'POST',
      body: JSON.stringify(batchData || {}),
    });
  }

  async assignInboundOrder(id: string, userId: string) {
    return this.request<any>(`/inbound-orders/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async releaseInboundOrder(id: string) {
    return this.request<any>(`/inbound-orders/${id}/release`, {
      method: 'POST',
    });
  }

  // Sets the order-level receiving intent (print mode) on a DRAFT order.
  // Item → ITEMS_ONLY | Box → BOX_ONLY | Both → BOX_AND_ITEMS.
  async setInboundOrderPrintMode(
    orderId: string,
    mode: 'ITEMS_ONLY' | 'BOX_ONLY' | 'BOX_AND_ITEMS',
  ) {
    return this.request<any>(`/inbound-orders/${orderId}/print-mode`, {
      method: 'PATCH',
      body: JSON.stringify({ mode }),
    });
  }

  // Warehouses
  async getWarehouses() {
    return this.request<any[]>('/warehouses');
  }

  async createWarehouse(data: any) {
    return this.request<any>('/warehouses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWarehouse(id: string, data: any) {
    return this.request<any>(`/warehouses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Locations
  // Get all locations with occupancy counts (for tree visualization)
  async getLocationsWithOccupancy(warehouseId?: string) {
    const query = warehouseId ? `?warehouseId=${warehouseId}` : '';
    return this.request<{
      locations: any[];
      occupancy: Record<string, number>;
    }>(`/locations/with-occupancy${query}`);
  }

  async createLocation(warehouseId: string, data: any) {
    return this.request<any>(`/locations`, {
      method: 'POST',
      body: JSON.stringify({ ...data, warehouseId }),
    });
  }

  async deleteLocation(locationId: string) {
    return this.request<{ success: boolean; message: string; deletedCount: number }>(`/locations/${locationId}`, {
      method: 'DELETE',
    });
  }

  async moveLocation(locationId: string, destinationId: string) {
    return this.request<any>(`/locations/${locationId}/move`, {
      method: 'PUT',
      body: JSON.stringify({ destinationId }),
    });
  }

  async getLocationInventory(locationId: string) {
    return this.request<{
      location: {
        id: string;
        warehouse: { code: string; name: string };
        zone: { code: string | null; name: string | null };
        aisle: string | null;
        rack: string | null;
        level: string | null;
        bin: string | null;
      };
      totalItems: number;
      products: Array<{
        productId: string;
        catalogNumber: string;
        name: string;
        totalQty: number;
        batchCount: number;
        statusBreakdown: Record<string, number>;
        batches: Array<{
          batchId: string | null;
          batchNo: string | null;
          qty: number;
          expiryDate: string | null;
          productionDate: string | null;
          items: Array<{
            id: string;
            epc: string | null;
            serialNumber: string | null;
          }>;
        }>;
      }>;
    }>(`/locations/${locationId}/inventory`);
  }

  // Auth & Registration
  async login(identifier: string, password: string) {
    return this.request<{ accessToken: string; user: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    });
  }

  async getCurrentUser() {
    return this.request<any>('/auth/me');
  }

  async getAvailableWarehouses() {
    return this.request<any[]>('/auth/warehouses');
  }

  async registerOrganization(data: {
    organizationName: string;
    ownerEmail: string;
    ownerName: string;
  }) {
    return this.request<{ message: string; organizationId: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async approveOrganization(organizationId: string) {
    return this.request<{ message: string; organization: any; resetLink?: string; ownerEmail?: string }>(`/auth/approve/${organizationId}`, {
      method: 'PUT',
      body: JSON.stringify({ organizationId }),
    });
  }

  // Suppliers
  async getSuppliers(options?: { search?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', options.limit.toString());
    const query = params.toString();
    return this.request<any[]>(`/suppliers${query ? `?${query}` : ''}`);
  }

  async searchSuppliers(query: string, limit = 10) {
    return this.getSuppliers({ search: query, limit });
  }

  async createSupplier(data: any) {
    return this.request<any>('/suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSupplier(id: string, data: any) {
    return this.request<any>(`/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSupplier(id: string) {
    return this.request<void>(`/suppliers/${id}`, {
      method: 'DELETE',
    });
  }

  // Customers
  async getCustomers() {
    return this.request<any[]>('/customers');
  }

  async searchCustomers(query: string, limit = 10) {
    // Client-side search since backend doesn't have search endpoint yet
    const customers = await this.getCustomers();
    const lowerQuery = query.toLowerCase();
    return customers
      .filter((c: any) =>
        c.code?.toLowerCase().includes(lowerQuery) ||
        c.name?.toLowerCase().includes(lowerQuery)
      )
      .slice(0, limit);
  }

  async createCustomer(data: {
    name: string;
    contactPerson?: string;
    email?: string;
    phone?: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    addressCountry?: string;
  }) {
    return this.request<any>('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomer(id: string, data: {
    name?: string;
    contactPerson?: string;
    email?: string;
    phone?: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    addressCountry?: string;
  }) {
    return this.request<any>(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCustomer(id: string) {
    return this.request<void>(`/customers/${id}`, {
      method: 'DELETE',
    });
  }

  // Reason Codes
  async getReasonCodes(scope?: 'INBOUND' | 'OUTBOUND' | 'ADJUSTMENT' | 'ALL') {
    const query = scope ? `?scope=${scope}` : '';
    return this.request<any[]>(`/reason-codes${query}`);
  }

  async createReasonCode(data: {
    code: string;
    description?: string;
    usageScope: 'INBOUND' | 'OUTBOUND' | 'ADJUSTMENT' | 'ALL';
    reasonType?: 'INCREASE' | 'DECREASE' | 'BOTH';
  }) {
    return this.request<any>('/reason-codes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateReasonCode(id: string, data: {
    code?: string;
    description?: string;
    usageScope?: 'INBOUND' | 'OUTBOUND' | 'ADJUSTMENT' | 'ALL';
    reasonType?: 'INCREASE' | 'DECREASE' | 'BOTH';
    isActive?: boolean;
  }) {
    return this.request<any>(`/reason-codes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteReasonCode(id: string) {
    return this.request<void>(`/reason-codes/${id}`, {
      method: 'DELETE',
    });
  }

  // Dispatch master data — drivers (also assistant drivers) and vehicle
  // plates. Read by anyone in the org; changed by administrators only
  // (settings.manage), and only here on the web.
  async getDrivers(includeInactive = false) {
    return this.request<Driver[]>(`/dispatch/drivers${includeInactive ? '?includeInactive=true' : ''}`);
  }

  async createDriver(data: { name: string; phone?: string; defaultVehicleId?: string | null; defaultAssistantDriverId?: string | null }) {
    return this.request<Driver>('/dispatch/drivers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDriver(id: string, data: { name?: string; phone?: string; defaultVehicleId?: string | null; defaultAssistantDriverId?: string | null; isActive?: boolean }) {
    return this.request<Driver>(`/dispatch/drivers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDriver(id: string) {
    return this.request<{ success: boolean }>(`/dispatch/drivers/${id}`, {
      method: 'DELETE',
    });
  }

  async getVehicles(includeInactive = false) {
    return this.request<Vehicle[]>(`/dispatch/vehicles${includeInactive ? '?includeInactive=true' : ''}`);
  }

  async createVehicle(data: { plate: string; description?: string }) {
    return this.request<Vehicle>('/dispatch/vehicles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVehicle(id: string, data: { plate?: string; description?: string; isActive?: boolean }) {
    return this.request<Vehicle>(`/dispatch/vehicles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVehicle(id: string) {
    return this.request<{ success: boolean }>(`/dispatch/vehicles/${id}`, {
      method: 'DELETE',
    });
  }

  async completeInbound(inboundOrderId: string, notes?: string) {
    return this.request<{ order: unknown; voidedItemCount: number }>(
      `/inbound-orders/${inboundOrderId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({ notes }),
      },
    );
  }

  async cancelInbound(inboundOrderId: string) {
    return this.request<{ order: any; deletedItemCount: number }>(
      `/inbound-orders/${inboundOrderId}/cancel-inbound`,
      { method: 'POST' },
    );
  }

  // Organizations (for admin dashboard)
  async getOrganizations() {
    return this.request<any[]>('/organizations');
  }

  async getOrganization(id: string) {
    return this.request<any>(`/organizations/${id}`);
  }

  async getOrganizationDetails(id: string) {
    return this.request<{
      organization: any;
      members: Array<{
        id: string;
        userId: string;
        roleId: string | null;
        isActive: boolean;
        user: {
          id: string;
          email: string | null;
          fullName: string | null;
        };
        role: {
          id: string;
          name: string;
        } | null;
      }>;
    }>(`/organizations/${id}/details`);
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Admin utilities
  async promoteToAdmin(email: string) {
    return this.request<{ message: string; user: any }>('/auth/promote-to-admin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async updateOrganization(id: string, data: Partial<any>) {
    return this.request<any>(`/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteOrganization(id: string) {
    return this.request<void>(`/organizations/${id}`, {
      method: 'DELETE',
    });
  }

  async resetOrgDataForTesting(orgId: string) {
    return this.request<{ message: string; counts: any }>(`/auth/reset-org-data-for-testing/${orgId}`, {
      method: 'POST',
    });
  }

  async resetOperationalData(orgId: string) {
    return this.request<{ message: string; counts: any }>(`/auth/reset-operational-data/${orgId}`, {
      method: 'POST',
    });
  }

  // Database Backup
  async listBackups(): Promise<Array<{ key: string; timestamp: string; sizeBytes: number; pgVersion: string; type: 'manual' | 'predeploy' | 'scheduled' }>> {
    return this.get('/database-backup/list');
  }

  async createBackup(): Promise<{ key: string; timestamp: string; sizeBytes: number; pgVersion: string; type: string }> {
    return this.post('/database-backup/create');
  }

  async downloadBackupByKey(key: string): Promise<Blob> {
    // key is like "backups/manual-2024-01-01T00-00-00-000Z.enc" — strip "backups/" prefix for the URL
    const urlKey = key.startsWith('backups/') ? key.slice('backups/'.length) : key;
    return this.requestBlob(`/database-backup/download/${urlKey}`);
  }

  async deleteBackup(key: string): Promise<{ message: string }> {
    const urlKey = key.startsWith('backups/') ? key.slice('backups/'.length) : key;
    return this.delete(`/database-backup/${urlKey}`);
  }

  async restoreBackupFromKey(key: string): Promise<{ message: string }> {
    return this.post('/database-backup/restore', { key });
  }

  /** @deprecated Use createBackup() + downloadBackupByKey() instead */
  async downloadBackup(): Promise<Blob> {
    return this.requestBlob('/database-backup/download');
  }

  async restoreBackup(file: File): Promise<{ message: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}/database-backup/restore`;
    const headers: Record<string, string> = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `Restore failed: ${response.statusText}`);
    }

    return response.json();
  }

  // Operator Management
  async generateBadgePdf(userId: string): Promise<Blob> {
    const url = `${this.baseUrl}/auth/badges/${userId}/pdf`;
    const headers: Record<string, string> = {};
    
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: `HTTP error! status: ${response.status}` };
      }
      throw new Error(errorData.message || `Failed to generate badge: ${response.status}`);
    }

    return response.blob();
  }

  async generateBadgeImage(userId: string): Promise<Blob> {
    const url = `${this.baseUrl}/auth/badges/${userId}/image`;
    const headers: Record<string, string> = {};
    
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: `HTTP error! status: ${response.status}` };
      }
      throw new Error(errorData.message || `Failed to generate badge: ${response.status}`);
    }

    return response.blob();
  }

  // Users
  async getUsers() {
    return this.request<any[]>('/auth/users');
  }

  async createUser(data: {
    fullName: string;
    username?: string;
    password?: string;
    email?: string;
    roleId?: string;
    warehouseId?: string;
  }) {
    return this.request<{
      user: any;
      username: string;
      badgeToken: string;
      resetLink?: string;
    }>('/auth/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // User Warehouse Assignments
  async setUserWarehouseAssignments(
    userId: string,
    warehouseIds: string[],
    defaultWarehouseId?: string,
  ) {
    return this.request<any[]>(`/auth/users/${userId}/warehouses`, {
      method: 'PUT',
      body: JSON.stringify({ warehouseIds, defaultWarehouseId }),
    });
  }

  async removeUserWarehouseAssignment(userId: string, warehouseId: string) {
    return this.request<any>(`/auth/users/${userId}/warehouses/${warehouseId}`, {
      method: 'DELETE',
    });
  }

  async setUserDefaultWarehouse(userId: string, warehouseId: string) {
    return this.request<any>(`/auth/users/${userId}/warehouses/${warehouseId}/default`, {
      method: 'PUT',
    });
  }

  // User Management
  async updateUser(userId: string, data: { fullName?: string; status?: string; username?: string; email?: string; password?: string }) {
    return this.request<any>(`/auth/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async assignUserRole(userId: string, roleId: string | null) {
    return this.request<any>(`/auth/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ roleId }),
    });
  }

  // Roles
  async getRoles() {
    return this.request<any[]>('/auth/roles');
  }

  async createRole(data: { name: string; description?: string }) {
    return this.request<any>('/auth/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRole(roleId: string, data: { name?: string; description?: string }) {
    return this.request<any>(`/auth/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRole(roleId: string) {
    return this.request<any>(`/auth/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  // Permissions
  async getPermissions() {
    return this.request<{ slug: string; description: string | null; groupName: string | null; forWeb: boolean; forMobile: boolean }[]>('/auth/permissions');
  }

  async getRolePermissions(roleId: string) {
    return this.request<{ web: string[]; mobile: string[] }>(`/auth/roles/${roleId}/permissions`);
  }

  async setRolePermissions(roleId: string, web: string[], mobile: string[]) {
    return this.request<{ web: string[]; mobile: string[] }>(`/auth/roles/${roleId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ web, mobile }),
    });
  }

  // Facilities
  async getFacilities() {
    return this.request<any[]>('/facilities');
  }

  async createFacility(data: {
    code: string;
    name: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    addressCountry?: string;
  }) {
    return this.request<any>('/facilities', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFacility(id: string, data: Partial<{
    code: string;
    name: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    addressCountry?: string;
    isActive?: boolean;
  }>) {
    return this.request<any>(`/facilities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFacility(id: string) {
    return this.request<any>(`/facilities/${id}`, {
      method: 'DELETE',
    });
  }

  // Sales Orders — the commercial layer above pick lists. One customer order,
  // one or more pick lists (one per contributing warehouse).
  async getSalesOrders(options?: {
    statuses?: string[];
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.search) params.append('search', options.search);
    if (options?.statuses?.length) params.append('statuses', options.statuses.join(','));
    const query = params.toString();
    return this.request<{
      items: Array<{
        id: string;
        salesOrderNumber: string;
        referenceNumber: string | null;
        customer: { id: string; name: string } | null;
        requestedDeliveryDate: string | null;
        createdAt: string;
        cancelledAt: string | null;
        status: 'OPEN' | 'IN_PROGRESS' | 'PARTIALLY_SHIPPED' | 'SHIPPED' | 'CANCELLED';
        pickListCount: number;
        warehouses: string[];
        // Un-released legs — lets the list page offer Start Picking directly.
        draftPickLists: Array<{ id: string; orderNumber: string }>;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/sales-orders${query ? `?${query}` : ''}`);
  }

  async getSalesOrder(id: string) {
    return this.request<{
      id: string;
      salesOrderNumber: string;
      referenceNumber: string | null;
      customer: { id: string; name: string } | null;
      requestedDeliveryDate: string | null;
      notes: string | null;
      createdAt: string;
      cancelledAt: string | null;
      cancellationReason: string | null;
      status: 'OPEN' | 'IN_PROGRESS' | 'PARTIALLY_SHIPPED' | 'SHIPPED' | 'CANCELLED';
      lines: Array<{
        id: string;
        productId: string;
        catalogNumber: string | null;
        productName: string | null;
        uom: string;
        quantityOrdered: number;
        quantityShipped: number;
        // ordered − shipped; 0 once cancelled. Picked-but-unshipped still counts.
        quantityRemaining: number;
        pickedAwaitingShipment: number;
      }>;
      totals: {
        ordered: number;
        shipped: number;
        remaining: number;
        pickedAwaitingShipment: number;
        overShipped: number;
      };
      pickLists: Array<{
        id: string;
        orderNumber: string;
        status: string;
        createdAt: string;
        warehouse: { id: string; code: string | null; name: string | null };
        totalOrdered: number;
        totalPicked: number;
        totalShipped: number;
        shipmentIds: string[];
        // Items taken off this list at packing (handheld "Cancel items").
        totalTakenOff: number;
        // Per product: ordered = ORIGINAL (current + taken off), ships = what
        // the list still asks for.
        items: Array<{
          productId: string;
          catalogNumber: string | null;
          productName: string | null;
          ordered: number;
          takenOff: number;
          ships: number;
          shipped: number;
        }>;
        takeOffs: SalesOrderTakeOff[];
      }>;
      shipments: Array<{
        id: string;
        shipmentNumber: string;
        // VERIFIED = parked for its truck, SHIPPED = departed history,
        // CANCELLED = re-bundled before departure.
        status: 'VERIFIED' | 'SHIPPED' | 'CANCELLED';
        verifiedAt: string | null;
        verifiedBy: { name: string | null } | null;
        // The staff member who tapped their NFC badge during the sweep —
        // empty when nobody tapped; verifiedBy (session) is NOT a fallback.
        markedVerifiedBy: { name: string | null } | null;
        shippedAt: string | null;
        shippedBy: { name: string | null } | null;
        // The staff member who tapped their NFC badge at departure — empty
        // when nobody tapped; shippedBy (the session user) is NOT a fallback.
        markedShippedBy: { name: string | null } | null;
        driverName: string | null;
        driverVehiclePlate: string | null;
        assistantDriverName: string | null;
        pickListIds: string[];
        // What this shipment carried, per pick list, at unit level.
        pickLists: Array<{
          id: string;
          orderNumber: string;
          warehouseCode: string | null;
          units: Array<{
            catalogNumber: string | null;
            productName: string | null;
            serialNumber: string | null;
            epc: string | null;
            batchNo: string | null;
            expiryDate: string | null;
          }>;
        }>;
      }>;
    }>(`/sales-orders/${id}`);
  }

  // Raise a follow-up pick list for what a sales order still owes. Quantities
  // are capped server-side at each product's remaining (ordered − shipped).
  async createSalesOrderFollowUp(
    id: string,
    items?: Array<{ productId: string; quantity: number }>,
  ) {
    return this.request<{
      salesOrderNumber: string;
      pickLists: Array<{
        id: string;
        orderNumber: string;
        warehouseId: string;
        lineCount: number;
      }>;
      routingIssues: Array<{ code: string; detail: string }>;
    }>(`/sales-orders/${id}/follow-up`, {
      method: 'POST',
      body: JSON.stringify(items && items.length > 0 ? { items } : {}),
    });
  }

  // Cancel a sales order: live pick lists cancel with putback recorded in
  // their own warehouse; shipped history stays; the order stamps who/when/why.
  async cancelSalesOrder(id: string, reason?: string) {
    return this.request<{
      salesOrderId: string;
      salesOrderNumber: string;
      cancelledAt: string;
      legs: Array<{
        id: string;
        orderNumber: string;
        action: string;
        itemsReverted: number;
      }>;
    }>(`/sales-orders/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify(reason?.trim() ? { reason: reason.trim() } : {}),
    });
  }

  // Outbound Orders
  async getOutboundOrders(options?: {
    limit?: number;
    offset?: number;
    statuses?: string[];
    warehouseId?: string;
    includeItems?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.statuses?.length) params.append('statuses', options.statuses.join(','));
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.includeItems) params.append('includeItems', 'true');
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.search) params.append('search', options.search);
    const query = params.toString();
    return this.request<{
      items: any[];
      total: number;
      limit: number;
      offset: number;
    }>(`/outbound-orders${query ? `?${query}` : ''}`);
  }

  async getOutboundOrderAllocatedItems(orderId: string) {
    return this.request<{
      allocatedItems: Array<{
        id: string;
        identifier: string;
        inventoryItemId: string;
        productId: string;
        productName: string;
        catalogNumber: string;
        epc: string | null;
        serialNumber: string | null;
        batchId: string | null;
        batchNo: string | null;
        expiryDate: string | null;
        allocatedAt: string;
        location: { zone: string | null; aisle: string | null; rack: string | null; level: string | null; bin: string | null; name?: string | null } | null;
      }>;
      totalAllocated: number;
    }>(`/outbound-orders/${orderId}/allocated-items`);
  }

  async startPicking(orderId: string, force = false) {
    return this.post<{
      success: boolean;
      shortages?: Array<{
        productId: string;
        productName: string;
        catalogNumber: string;
        required: number;
        available: number;
      }>;
    }>(`/outbound-orders/${orderId}/start-picking${force ? '?force=true' : ''}`);
  }

  async createOutboundOrder(data: {
    orderNumber?: string;
    referenceNumber?: string;
    warehouseId: string;
    customerId?: string;
    requestedDeliveryDate?: string;
    notes?: string;
    items: Array<{
      productId: string;
      quantity: number;
    }>;
  }) {
    return this.request<{ order: any }>('/outbound-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getOutboundOrder(id: string) {
    return this.request<any>(`/outbound-orders/${id}`);
  }

  async getOutboundOrderItems(orderId: string) {
    return this.request<Array<{
      id: string;
      productId: string;
      uom: string;
      quantityOrdered: number;
      quantityPicked: number;
      quantityShipped: number;
      product?: { id: string; name: string; catalogNumber: string } | null;
    }>>(`/outbound-orders/${orderId}/items`);
  }

  async updateOutboundOrder(id: string, data: {
    referenceNumber?: string | null;
    customerId?: string | null;
    requestedDeliveryDate?: string | null;
    notes?: string | null;
  }) {
    return this.request<any>(`/outbound-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async addOutboundItem(orderId: string, data: { productId: string; quantity: number; uom?: string }) {
    return this.request<any>(`/outbound-orders/${orderId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOutboundItem(orderId: string, itemId: string, data: { quantity: number }) {
    return this.request<any>(`/outbound-orders/${orderId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteOutboundItem(orderId: string, itemId: string) {
    return this.request<any>(`/outbound-orders/${orderId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async getStockList(options?: {
    warehouseId?: string;
    facilityId?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    showExpired?: boolean;
    statuses?: string[];
  }) {
    const params = new URLSearchParams();
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.facilityId) params.append('facilityId', options.facilityId);
    if (options?.search) params.append('search', options.search);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.showExpired) params.append('showExpired', 'true');
    if (options?.statuses) params.append('statuses', options.statuses.join(','));
    const query = params.toString() ? `?${params.toString()}` : '';

    return this.request<{
      items: Array<{
        productId: string;
        catalogNumber: string;
        name: string;
        type: string;
        baseUom: string;
        category: string | null;
        totalOnHand: number;
        receivedQty: number;
        availableQty: number;
        boxedQty: number;
        allocatedQty: number;
        expiredQty: number;
        batchCount: number;
        reorderPoint: number;
        minStockLevel: number;
        componentProductId: string | null;
        unitsPerPack: number | null;
        stockStatus: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'CRITICAL';
        batches: Array<{
          batchId: string | null;
          batchNo: string | null;
          quantity: number;
          availableQty: number;
          productionDate: string | null;
          expiryDate: string | null;
          daysUntilExpiry: number | null;
          locationId: string | null;
          items: Array<{
            id: string;
            epc: string;
            serialNumber: string | null;
            status: string;
            quantity: number;
            locationId: string | null;
            location: {
              zone: string | null;
              aisle: string | null;
              rack: string | null;
              level: string | null;
              bin: string | null;
            } | null;
            containerId: string | null;
            containerLpn: string | null;
            outboundOrderNumber: string | null;
            outboundOrderId: string | null;
          }>;
        }>;
      }>;
      total: number;
      totalOnHand: number;
      totalExpired: number;
      limit: number;
      offset: number;
    }>(`/stock-list${query}`);
  }

  /**
   * Detailed stock: one row per product x location x batch, filtered and paged
   * on the server. The export walks every page with the same filters.
   */
  async getStockDetail(params: StockDetailParams): Promise<StockDetailResponse> {
    return this.request<StockDetailResponse>(`/stock-list/detail?${stockDetailQuery(params)}`);
  }

  /** The same filtered stock summed per product (the export's "By product" sheet). */
  async getStockDetailByProduct(params: StockDetailParams): Promise<StockDetailPage<StockDetailProductRow>> {
    return this.request<StockDetailPage<StockDetailProductRow>>(
      `/stock-list/detail/by-product?${stockDetailQuery(params)}`,
    );
  }

  /** The location tree the stock filter picks from: allowed warehouses and their locations. */
  async getStockLocationOptions(warehouseId?: string): Promise<StockLocationOptions> {
    const query = warehouseId ? `?warehouseId=${warehouseId}` : '';
    return this.request<StockLocationOptions>(`/stock-list/locations${query}`);
  }

  /** Category choices for the stock filters (the org's list, then any other value in use). */
  async getStockCategories(): Promise<string[]> {
    return this.request<string[]>('/stock-list/categories');
  }

  async getProductItems(productId: string, options?: {
    warehouseId?: string;
    facilityId?: string;
    showExpired?: boolean;
    statuses?: string[];
  }): Promise<Array<{
    id: string;
    epc: string;
    serialNumber: string | null;
    status: string;
    quantity: number;
    locationId: string | null;
    location: {
      zone: string | null;
      aisle: string | null;
      rack: string | null;
      level: string | null;
      bin: string | null;
    } | null;
    containerId: string | null;
    containerLpn: string | null;
    batchNo: string | null;
    expiryDate: string | null;
    outboundOrderNumber: string | null;
    outboundOrderId: string | null;
  }>> {
    const params = new URLSearchParams();
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.facilityId) params.append('facilityId', options.facilityId);
    if (options?.showExpired) params.append('showExpired', 'true');
    if (options?.statuses) params.append('statuses', options.statuses.join(','));
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/stock-list/product-items/${productId}${query}`);
  }

  // ============================================================================
  // LABEL TEMPLATES (offline cached for printing)
  // ============================================================================

  // Sync label templates to local cache
  async syncLabelTemplates(): Promise<void> {
    if (!this.authToken) {
      throw new Error('Auth token required for sync');
    }
    return localStore.syncLabelTemplates(this.baseUrl, this.authToken);
  }

  // ============================================================================
  // STOCK TAKE (uses stock_takes, stock_take_scans, stock_take_results tables)
  // ============================================================================

  async getStockTakes(options?: { statuses?: string[]; warehouseId?: string; limit?: number; offset?: number; sortBy?: string; sortOrder?: 'asc' | 'desc'; search?: string }) {
    const params = new URLSearchParams();
    if (options?.statuses?.length) params.append('statuses', options.statuses.join(','));
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.search) params.append('search', options.search);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: StockTakeTask[]; total: number; limit: number; offset: number }>(`/stock-takes${query}`);
  }

  async completeStockTake(id: string) {
    return this.request<StockTakeTask>(`/stock-takes/${id}/complete`, {
      method: 'POST',
    });
  }

  async cancelStockTake(id: string) {
    return this.request<StockTakeTask>(`/stock-takes/${id}/cancel`, {
      method: 'POST',
    });
  }

  /** The count page: who started / finished / cancelled it, its totals, every session (newest first). */
  async getStockTakeSessions(id: string) {
    return this.request<StockTakeOverview>(`/stock-takes/${id}/sessions`);
  }

  /** Finish: the count goes for review, whatever is still being scanned. Returns the overview. */
  async finishStockTake(id: string) {
    return this.request<StockTakeOverview>(`/stock-takes/${id}/finish`, {
      method: 'POST',
    });
  }

  /** One session's complete history, newest first (limit ≤ 50000, default 100). */
  async getStockTakeSessionReads(
    id: string,
    number: number,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set('limit', options.limit.toString());
    if (options?.offset != null) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<{
      session: StockTakeSessionRow;
      items: StockTakeSessionRead[];
      total: number;
      limit: number;
      offset: number;
    }>(`/stock-takes/${id}/sessions/${number}/reads${qs ? `?${qs}` : ''}`);
  }

  /**
   * `explain` (the review page): expected units at their snapshot shelf, units that arrived
   * during the count, and `reconciled` saying what happened to each unit it can't account for.
   */
  async getStockTakeScannedItems(
    id: string,
    limit?: number,
    offset?: number,
    locationId?: string,
    explain?: boolean,
  ) {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', limit.toString());
    if (offset != null) params.set('offset', offset.toString());
    if (locationId != null) params.set('locationId', locationId);
    if (explain) params.set('explain', 'true');
    const qs = params.toString();
    return this.request<{
      items: Array<{
        id: string;
        epc: string | null;
        serialNumber: string | null;
        catalogNumber: string | null;
        productName: string | null;
        batchNo: string | null;
        expiryDate: string | null;
        status: string;
        scanned: boolean;
        expected: boolean;
        location: {
          zone: string | null;
          aisle: string | null;
          rack: string | null;
          level: string | null;
          bin: string | null;
        } | null;
        reconciled?: ReconciledInfo | null;
        // Every session that read it, in read order (explain only; empty before sessions).
        readers?: StockTakeSessionReader[];
      }>;
      // Sealed/deconsolidating boxes counted by their LPN. Contents are
      // summarized (itemCount/skuCount); member EPCs are intentionally absent.
      // Returned in full on every page (not paginated by total/limit/offset).
      boxes: Array<{
        unitType: 'BOX';
        containerId: string;
        lpnCode: string;
        state: string;
        itemCount: number;
        skuCount: number;
        // Per-catalog breakdown of the box's countable contents (absent on old backends).
        contents?: Array<{
          catalogNumber: string | null;
          productName: string | null;
          batchNo: string | null;
          expiryDate: string | null;
          qty: number;
        }>;
        scanned: boolean;
        expected: boolean;
        location: {
          zone: string | null;
          aisle: string | null;
          rack: string | null;
          level: string | null;
          bin: string | null;
        } | null;
        reconciled?: ReconciledInfo | null;
        readers?: StockTakeSessionReader[];
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/stock-takes/${id}/scanned-items${qs ? `?${qs}` : ''}`);
  }

  async getAllStockTakeScannedItems(id: string, locationId?: string, explain?: boolean) {
    const allItems: Awaited<ReturnType<typeof this.getStockTakeScannedItems>>['items'] = [];
    let offset = 0;
    const limit = 500;

    while (true) {
      const response = await this.getStockTakeScannedItems(id, limit, offset, locationId, explain);
      allItems.push(...response.items);
      offset += response.items.length;
      if (offset >= response.total || response.items.length === 0) break;
    }

    return allItems;
  }

  /**
   * Sealed/deconsolidating boxes (LPN units) for a stock take. Boxes come back
   * in full on any page, so a single small page is enough.
   */
  async getStockTakeBoxes(id: string, locationId?: string, explain?: boolean) {
    const res = await this.getStockTakeScannedItems(id, 1, 0, locationId, explain);
    return res.boxes ?? [];
  }

  async getStockTakeLocationProgress(id: string) {
    return this.request<{
      locations: Array<{
        locationId: string | null;
        locationCode: string;
        locationType: string | null;
        zone: string | null;
        aisle: string | null;
        rack: string | null;
        level: string | null;
        bin: string | null;
        expectedCount: number;
        foundCount: number;
        missingCount: number;
        extraCount: number;
      }>;
    }>(`/stock-takes/${id}/location-progress`);
  }

  // ============================================================================
  // INVENTORY VARIANCES & SIGHTINGS
  // ============================================================================

  async getMissingItems(options?: {
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    warehouseId?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset != null) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.search) params.set('search', options.search);
    if (options?.warehouseId) params.set('warehouseId', options.warehouseId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      items: Array<{
        id: string;
        epc: string | null;
        serialNumber: string | null;
        status: string;
        missingSince: string | null;
        product?: { id: string; name: string; catalogNumber: string | null } | null;
        lastKnownLocation?: { id: string; code: string } | null;
        lastSeenAt?: string | null;
        lastSeenNear?: { id: string; zone?: string | null; aisle?: string | null; rack?: string | null; level?: string | null; bin?: string | null } | null;
        lastSeenBy?: { id: string; name: string | null } | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/inventory/items/missing${query}`);
  }

  async breakdownBulkItem(itemId: string, warehouseId: string) {
    return this.request<{ itemId: string; childCount: number }>(`/inventory/items/${itemId}/breakdown`, {
      method: 'POST',
      body: JSON.stringify({ warehouseId }),
    });
  }

  async recoverMissingItem(itemId: string, locationId: string, notes?: string) {
    return this.request<{ success: boolean; message?: string }>(
      `/inventory/items/${itemId}/recover`,
      {
        method: 'POST',
        body: JSON.stringify({ locationId, notes }),
      }
    );
  }

  async getInventoryItemHistory(itemId: string) {
    return this.request<{
      item: {
        id: string;
        epc: string | null;
        serialNumber: string | null;
        status: string;
        batchNo: string | null;
        expiryDate: string | null;
        createdAt: string;
        updatedAt: string;
        product: { id: string; catalogNumber: string; name: string } | null;
        location: { id: string; zone: string | null; aisle: string | null; rack: string | null; level: string | null; bin: string | null } | null;
      };
      inboundOrder: {
        id: string;
        orderNumber: string;
        status: string;
      } | null;
      outboundOrder: {
        id: string;
        orderNumber: string;
        status: string;
      } | null;
      transactions: Array<{
        id: string;
        transactionType: string;
        referenceId: string | null;
        referenceType: string | null;
        quantityChange: string;
        notes: string | null;
        timestamp: string;
        fromLocationId: string | null;
        toLocationId: string | null;
        user: { id: string; username: string | null; fullName: string | null } | null;
      }>;
      printHistory: Array<{
        id: string;
        status: string;
        printerName: string | null;
        printerAddress: string | null;
        errorMessage: string | null;
        printedAt: string;
        user: { id: string; username: string | null; fullName: string | null } | null;
      }>;
    }>(`/inventory/items/${itemId}/history`);
  }

  // ============================================================================
  // PRINT QUEUE (uses print_jobs table)
  // ============================================================================

  async getPrintQueueStats(filters?: { warehouseId?: string; facilityId?: string }) {
    const params = new URLSearchParams();
    if (filters?.warehouseId) params.append('warehouseId', filters.warehouseId);
    if (filters?.facilityId) params.append('facilityId', filters.facilityId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<PrintQueueStats>(`/print-queue/stats${query}`);
  }

  async getPrintJobs(filters?: PrintJobFilters) {
    const params = new URLSearchParams();
    if (filters?.facilityId) params.append('facilityId', filters.facilityId);
    if (filters?.warehouseId) params.append('warehouseId', filters.warehouseId);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.batchId) params.append('batchId', filters.batchId);
    if (filters?.referenceId) params.append('referenceId', filters.referenceId);
    if (filters?.referenceType) params.append('referenceType', filters.referenceType);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset != null) params.append('offset', filters.offset.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: PrintJob[]; total: number }>(`/print-queue${query}`);
  }

  async getPrintJob(id: string) {
    return this.request<PrintJob>(`/print-queue/${id}`);
  }

  async updatePrintJobStatus(id: string, data: { status: string; errorMessage?: string; printedLabels?: number; failedLabels?: number }) {
    return this.request<PrintJob>(`/print-queue/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async cancelPrintJob(id: string) {
    return this.request<PrintJob>(`/print-queue/${id}/cancel`, {
      method: 'POST',
    });
  }

  async getPrintJobLabels(jobId: string) {
    const labels = await this.request<any[]>(`/print-queue/${jobId}/labels`, {
      method: 'GET',
    });
    return labels;
  }

  // ============================================================================
  // DISPOSAL
  // ============================================================================

  async getDisposals(options?: {
    search?: string;
    status?: string;
    statuses?: string[];
    warehouseId?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.append('search', options.search);
    if (options?.statuses?.length) params.append('statuses', options.statuses.join(','));
    else if (options?.status) params.append('status', options.status);
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: any[]; total: number; limit: number; offset: number }>(`/disposal${query}`);
  }

  async getDisposal(id: string) {
    return this.request<any>(`/disposal/${id}`);
  }

  async getDisposableItems(options: {
    warehouseId: string;
    search?: string;
    statusFilter?: 'expired' | 'missing';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    params.append('warehouseId', options.warehouseId);
    if (options.search) params.append('search', options.search);
    if (options.statusFilter) params.append('statusFilter', options.statusFilter);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset != null) params.append('offset', options.offset.toString());
    return this.request<{ products: DisposableProduct[]; total: number; limit: number; offset: number }>(`/disposal/items?${params.toString()}`);
  }

  async getDisposableBoxes(options: {
    warehouseId: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    params.append('warehouseId', options.warehouseId);
    if (options.search) params.append('search', options.search);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset != null) params.append('offset', options.offset.toString());
    return this.request<{ boxes: DisposableBox[]; total: number; limit: number; offset: number }>(`/disposal/boxes?${params.toString()}`);
  }

  async createDisposal(data: {
    warehouseId: string;
    itemIds: string[];
    reasonCodeId?: string;
    notes?: string;
  }) {
    return this.request<any>('/disposal', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async assignDisposal(id: string, userId: string) {
    return this.request<any>(`/disposal/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async unassignDisposal(id: string) {
    return this.request<any>(`/disposal/${id}/unassign`, {
      method: 'POST',
    });
  }

  async completeDisposal(id: string) {
    return this.request<{ disposedCount: number; disposal?: unknown }>(`/disposal/${id}/complete`, {
      method: 'POST',
    });
  }

  async confirmDisposal(id: string) {
    return this.request<any>(`/disposal/${id}/confirm`, {
      method: 'POST',
    });
  }

  async rejectDisposal(id: string) {
    return this.request<any>(`/disposal/${id}/reject`, {
      method: 'POST',
    });
  }

  async cancelDisposal(id: string) {
    return this.request<any>(`/disposal/${id}/cancel`, {
      method: 'POST',
    });
  }

  // ============================================================================
  // RELOCATION
  // ============================================================================

  async getRelocations(options?: {
    search?: string;
    status?: string;
    statuses?: string[];
    warehouseId?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.append('search', options.search);
    if (options?.statuses?.length) params.append('statuses', options.statuses.join(','));
    else if (options?.status) params.append('status', options.status);
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: any[]; total: number; limit: number; offset: number }>(`/relocation${query}`);
  }

  async getRelocation(id: string) {
    return this.request<any>(`/relocation/${id}`);
  }

  async getRelocatableItems(options: {
    warehouseId: string;
    search?: string;
    statusFilter?: 'expired';
    sourceLocationId?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    params.append('warehouseId', options.warehouseId);
    if (options.search) params.append('search', options.search);
    if (options.statusFilter) params.append('statusFilter', options.statusFilter);
    if (options.sourceLocationId) params.append('sourceLocationId', options.sourceLocationId);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset != null) params.append('offset', options.offset.toString());
    return this.request<{ products: DisposableProduct[]; total: number; limit: number; offset: number }>(`/relocation/items?${params.toString()}`);
  }

  async createRelocation(data: {
    warehouseId: string;
    targetLocationId: string;
    itemIds: string[];
    notes?: string;
  }) {
    return this.request<any>('/relocation', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async assignRelocation(id: string, userId: string) {
    return this.request<any>(`/relocation/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async unassignRelocation(id: string) {
    return this.request<any>(`/relocation/${id}/unassign`, {
      method: 'POST',
    });
  }

  async confirmRelocation(id: string) {
    return this.request<any>(`/relocation/${id}/confirm`, {
      method: 'POST',
    });
  }

  async cancelRelocation(id: string) {
    return this.request<any>(`/relocation/${id}/cancel`, {
      method: 'POST',
    });
  }

  async searchLocations(warehouseId: string, query: string, limit?: number) {
    const params = new URLSearchParams();
    params.append('warehouseId', warehouseId);
    params.append('q', query);
    if (limit) params.append('limit', limit.toString());
    return this.request<{ id: string; displayCode: string }[]>(`/locations/search?${params.toString()}`);
  }

  async reprintInventoryItemLabel(inventoryItemId: string) {
    return this.request<{ success: boolean; zpl?: string; error?: string; jobId?: string; labelIdentifier?: string }>(
      `/print-queue/reprint-by-item/${inventoryItemId}`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    );
  }

  async getLabelPrintHistory(jobId: string, labelIdentifier: string) {
    return this.request<any[]>(`/print-queue/${jobId}/labels/${labelIdentifier}/history`, {
      method: 'GET',
    });
  }

  async confirmLabelPrinted(jobId: string, labelIdentifier: string) {
    return this.request<any>(`/print-queue/${jobId}/labels/${labelIdentifier}/confirm-printed`, {
      method: 'POST',
    });
  }

  async bulkCreateLabelHistory(
    jobId: string,
    entries: Array<{
      // null for container/location labels: they are not inventory rows, and
      // sending an id that isn't one violates the history FK.
      inventoryItemId?: string | null;
      labelIdentifier: string;
      labelIndex?: number;
      status: 'SENT' | 'SUCCESS' | 'FAILED' | 'CANCELLED';
      printerName?: string;
      printerAddress?: string;
      printerPort?: number;
      errorMessage?: string;
      errorCode?: string;
      zplCode?: string;
    }>,
  ) {
    return this.request<any[]>(`/print-queue/${jobId}/labels/history/bulk`, {
      method: 'POST',
      body: JSON.stringify({ entries }),
    });
  }

  // ============================================================================
  // EXPIRY MONITOR
  // ============================================================================

  async getExpirySummary(warehouseId: string) {
    return this.request<ExpirySummary>(`/expiry/summary?warehouseId=${encodeURIComponent(warehouseId)}`);
  }

  // ============================================================================
  // REPORTS
  // ============================================================================

  async getReportInventory(options: {
    warehouseId?: string;
    search?: string;
    includeZeroStock?: boolean;

    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.search) params.append('search', options.search);
    if (options.includeZeroStock) params.append('includeZeroStock', 'true');

    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<{ items: ReportInventoryItem[]; total: number }>(`/reports/inventory?${params.toString()}`);
  }

  async getReportMovement(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    transactionType?: string;
    search?: string;

    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.transactionType) params.append('transactionType', options.transactionType);
    if (options.search) params.append('search', options.search);

    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<{ items: ReportMovementItem[]; total: number }>(`/reports/movement?${params.toString()}`);
  }

  async getStockCardSummary(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<{
      items: StockCardSummaryRow[];
      total: number;
      limit: number;
      offset: number;
      period: { dateFrom: string; dateTo: string };
    }>(`/reports/stock-card?${params.toString()}`);
  }

  async getStockCardLedger(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.search) params.append('search', options.search);
    return this.request<{
      items: Array<{
        catalogNumber: string;
        productName: string | null;
        timestamp: string | null;
        direction: StockCardDirection;
        subcategory: string;
        referenceNumber: string | null;
        externalReference: string | null;
        partyName: string | null;
        /** Disposal lines: the reason picked + the note typed. */
        reason: string | null;
        count: number;
        qtyIn: number;
        qtyOut: number;
        pendingDelta: number;
        availableDelta: number;
        allocatedDelta: number;
      }>;
      total: number;
      truncated?: boolean;
      period: { dateFrom: string; dateTo: string };
    }>(`/reports/stock-card-ledger?${params.toString()}`);
  }

  // ── Async stock-card PDF jobs ──
  async createStockCardJob(body: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    locale?: string;
    title?: string;
    filters?: string[];
    labels?: Record<string, string>;
    eventLabels?: Record<string, string>;
  }) {
    return this.post<StockCardJob>('/reports/stock-card-jobs', body);
  }

  async getStockCardJobs(options: { warehouseId?: string; limit?: number; offset?: number } = {}) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.limit != null) params.append('limit', String(options.limit));
    if (options.offset != null) params.append('offset', String(options.offset));
    return this.request<{
      items: StockCardJob[];
      total: number;
      limit: number;
      offset: number;
    }>(`/reports/stock-card-jobs?${params.toString()}`);
  }

  async downloadStockCardJob(id: string): Promise<Blob> {
    return this.requestBlob(`/reports/stock-card-jobs/${id}/download`);
  }

  async getStockCardDetail(catalogNumber: string, options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.limit != null) params.append('limit', options.limit.toString());
    if (options.offset != null) params.append('offset', options.offset.toString());
    return this.request<{
      catalogNumber: string;
      productName: string | null;
      // Live status snapshots (lifecycle breakdown):
      available: number;        // status='AVAILABLE' (on shelf)
      received: number;         // status='RECEIVED' (in receiving dock)
      storing: number;          // status='STORING' (mid-putaway)
      allocated: number;        // status IN ('ALLOCATED','PICKED') (staging)
      boxed: number;            // status='STORED' (sealed box) — nominal, separate from available
      putbackPending: number;   // status='PUTBACK_PENDING' — picked-then-cancelled, awaiting reshelve
      // Industry-standard totals:
      availableToSell: number;  // AVAILABLE_STATUSES sum (excludes boxed + putback)
      totalOnHand: number;      // physical inventory (availableToSell + allocated + boxed + putback)
      // Window flows (measured at/within the window, not live):
      windowIn: number;        // became available (putaway completed) in window
      windowPendingIn: number; // NEW arrivals still in dock at window end
      windowFound: number;
      windowOut: number;             // physical departures, deduped (closing math)
      windowAllocated: number;       // items allocated (committed) during window
      windowShipped: number;         // items shipped (dispatched) during window
      windowDisposed: number;
      windowReturned: number;         // came back via RETURN orders during window
      lost: number;
      // Ledger snapshots (opening at dateFrom, closing at dateTo;
      // total = available + pending; allocated is excluded from both):
      opening: number;
      openingAvailable: number;
      openingPending: number;
      openingAllocated: number;
      openingTotal: number;
      closing: number;
      closingAvailable: number;
      closingPending: number;
      closingAllocated: number;
      closingTotal: number;
      // Windowed putback-pending dimension. opening/closing = pending at the
      // window edges; new = cancelled-from-allocated inflow during the window;
      // done = reshelved outflow during the window. Excluded from opening/closing
      // totals until reshelved. closingPutback = openingPutback + new - done.
      openingPutback: number;
      windowPutbackNew: number;
      windowPutbackDone: number;
      closingPutback: number;
      // Windowed boxed dimension. opening/closing = units sealed in a box at the
      // window edges (excluded from totals until unboxed); new = units boxed
      // during the window; unboxed = units taken out of a box during the window
      // (these raise Closing Available / the Total).
      openingBoxed: number;
      windowBoxedNew: number;
      windowUnboxed: number;
      closingBoxed: number;
      items: StockCardEventRow[];
      total: number;
      limit: number;
      offset: number;
      period: { dateFrom: string; dateTo: string };
    }>(`/reports/stock-card/${encodeURIComponent(catalogNumber)}/detail?${params.toString()}`);
  }

  async getReportStockTake(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<ReportStockTakeData>(`/reports/stocktake?${params.toString()}`);
  }

  async getReportInbound(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    search?: string;

    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.status) params.append('status', options.status);
    if (options.search) params.append('search', options.search);

    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<ReportInboundData>(`/reports/inbound?${params.toString()}`);
  }

  async getReportOutbound(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    search?: string;

    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.status) params.append('status', options.status);
    if (options.search) params.append('search', options.search);

    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<ReportOutboundData>(`/reports/outbound?${params.toString()}`);
  }

  async getExpiryItems(options: {
    warehouseId: string;
    window?: string;
    search?: string;
    sortBy?: string;
    sortDir?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    params.append('warehouseId', options.warehouseId);
    if (options.window) params.append('window', options.window);
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortDir) params.append('sortDir', options.sortDir);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset != null) params.append('offset', options.offset.toString());
    return this.request<{ items: ExpiryBatchItem[]; total: number; limit: number; offset: number }>(`/expiry/items?${params.toString()}`);
  }

  // ============================================================================
  // REPORT LINE ITEMS (for detailed exports)
  // ============================================================================

  async getInventoryLineItems(productIds: string[], warehouseId?: string) {
    return this.post<InventoryLineItem[]>('/reports/inventory/line-items', { productIds, warehouseId });
  }

  async getInboundLineItems(orderIds: string[]) {
    return this.post<InboundLineItem[]>('/reports/inbound/line-items', { orderIds });
  }

  async getOutboundLineItems(orderIds: string[]) {
    return this.post<OutboundLineItem[]>('/reports/outbound/line-items', { orderIds });
  }

  async getStockTakeLineItems(stockTakeIds: string[]) {
    return this.post<StockTakeLineItem[]>('/reports/stocktake/line-items', { stockTakeIds });
  }

  async getCombinedReviewItems(stockTakeIds: string[]) {
    return this.post<CombinedReviewItem[]>('/reports/stocktake/combined-review-items', { stockTakeIds });
  }

  async getCombinedMovement(stockTakeIds: string[]) {
    return this.post<CombinedMovementResponse | null>('/reports/stocktake/combined-movement', { stockTakeIds });
  }

  async getCombinedMissingDetail(stockTakeIds: string[]) {
    return this.post<CombinedMissingDetailResponse>('/reports/stocktake/combined-missing-detail', { stockTakeIds });
  }

  async getCombinedInboundOutbound(stockTakeIds: string[]) {
    return this.post<CombinedInboundOutboundResponse>('/reports/stocktake/combined-inbound-outbound', { stockTakeIds });
  }

  // ============================================================================
  // TASKS
  // ============================================================================

  async getTasks(params: URLSearchParams) {
    return this.get<{ items: any[]; total: number; limit: number; offset: number }>(`/tasks?${params.toString()}`);
  }

  async getTaskScannedItems(taskId: string) {
    return this.get<any[]>(`/tasks/${taskId}/scanned-items`);
  }

  // Put-away boxes (containers holding STORED members) for the UNBOX tab,
  // each with a derived open-state. Warehouse-scoped, paginated.
  async getStoredBoxes(params: { warehouseId: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    qs.set('warehouseId', params.warehouseId);
    if (params.limit) qs.set('limit', params.limit.toString());
    if (params.offset != null) qs.set('offset', params.offset.toString());
    return this.get<{ items: StoredBox[]; total: number; limit: number; offset: number }>(
      `/containers/stored-boxes?${qs.toString()}`,
    );
  }

  // Cancel an unbox started by mistake: the box returns to SEALED, labels
  // minted at open are withdrawn and the queued label job is cancelled. The
  // server refuses once any label was printed or any member scanned. The note
  // is mandatory — it is journaled on every member and on the cancelled job.
  async cancelBoxOpen(containerId: string, notes: string) {
    return this.request<CancelBoxOpenResult>(`/containers/${containerId}/cancel-open`, {
      method: 'POST',
      body: JSON.stringify({ notes: notes.trim() }),
    });
  }

  // Putback queue: units awaiting return to storage (status PUTBACK_PENDING) —
  // a cancelled pick left them at the packing area. Warehouse-scoped.
  async getPutbackQueue(params: { warehouseId: string }) {
    const qs = new URLSearchParams();
    qs.set('warehouseId', params.warehouseId);
    return this.get<{ items: PutbackItem[]; total: number }>(
      `/putback/queue?${qs.toString()}`,
    );
  }

  // Putback v2 — read-only oversight (level 1): one row per cancellation
  // EPISODE, with pending/completed unit counts. Warehouse-scoped.
  async getPutbackOutbounds(params: {
    status?: string;
    search?: string;
    warehouseId?: string;
  }) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    if (params.warehouseId) qs.set('warehouseId', params.warehouseId);
    return this.get<PutbackOutboundsResponse>(`/putback/outbounds?${qs.toString()}`);
  }

  // Putback v2 — read-only oversight (level 2): item lines for one episode.
  async getPutbackLines(headerId: string, status?: string) {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    const suffix = qs.toString();
    return this.get<PutbackLinesResponse>(
      `/putback/outbounds/${headerId}/lines${suffix ? `?${suffix}` : ''}`,
    );
  }

  // Putback v2 — read-only oversight (level 3): unit-level detail for one line.
  async getPutbackLine(lineId: string) {
    return this.get<PutbackLineDetail>(`/putback/lines/${lineId}`);
  }

  // ============================================================================
  // PUTAWAY
  // ============================================================================

  async cancelPutawayTask(taskId: string) {
    return this.post(`/inventory/workflow/putaway/tasks/${taskId}/cancel`);
  }

  // Putaway page, per inbound order: units put away / total counting every
  // putaway screen (own tasks + Putaway by Scan), with the order's tasks and
  // the by-scan sessions that touched it. Paginated by order.
  async getPutawayOrderProgress<T = any>(params: URLSearchParams) {
    return this.get<{ items: T[]; total: number; limit: number; offset: number }>(
      `/inventory/putaway/orders?${params.toString()}`,
    );
  }

  // ============================================================================
  // OUTBOUND ORDER CANCEL
  // ============================================================================

  async cancelOutboundOrder(id: string, finalizeCancel = true) {
    const query = finalizeCancel ? '?finalizeCancel=true' : '';
    return this.post(`/outbound-orders/${id}/cancel${query}`);
  }

  async resumePicking(orderId: string) {
    return this.post(`/outbound-orders/${orderId}/resume-picking`);
  }

  async cancelRemainingItems(orderId: string) {
    return this.post(`/outbound-orders/${orderId}/cancel-remaining`);
  }

  // ============================================================================
  // LOCATION MANAGEMENT
  // ============================================================================

  async updateLocation(id: string, data: any) {
    return this.put(`/locations/${id}`, data);
  }

  async renameLocationSegment(data: any) {
    return this.put('/locations/rename-segment', data);
  }

  async generateLocationsDynamic(config: any) {
    return this.post<{ created: number; conflicts: number; conflictCodes: string[]; message: string }>('/locations/batch-create-dynamic', config);
  }

  // ============================================================================
  // WAREHOUSE DELETE
  // ============================================================================

  async deleteWarehouse(id: string) {
    return this.delete(`/warehouses/${id}`);
  }

  // ============================================================================
  // ORGANIZATION SETTINGS
  // ============================================================================

  async updateLocationDisplaySettings(configs: any) {
    return this.put('/organizations/settings/location-display', configs);
  }

  // ============================================================================
  // INBOUND ORDER UPDATE
  // ============================================================================

  async updateInboundOrder(id: string, data: any) {
    return this.put(`/inbound-orders/${id}`, data);
  }

  // ============================================================================
  // INBOUND IMPORT (shared by the Apps Script integration)
  // ============================================================================

  /** Create (or dry-run) inbound orders from parsed boxes. */
  async importInboundOrders(
    payload: InboundImportPayload,
  ): Promise<InboundImportResponse> {
    return this.post<InboundImportResponse>('/inbound-orders/import', payload);
  }

  // ==========================================================================
  // GAS WEB APP INTEGRATION (inbound import, no OAuth — Apps Script /exec URL)
  // ==========================================================================

  /** The org's saved Apps Script connection (no token), or { connected: false }. */
  async getGasConnection(): Promise<GasConnectionStatus> {
    return this.get<GasConnectionStatus>('/integrations/gas/connection');
  }

  /** Save (or replace) the org's /exec URL + token. */
  async saveGasConnection(
    execUrl: string,
    token?: string,
    displayName?: string,
  ): Promise<GasConnectionStatus> {
    return this.post<GasConnectionStatus>('/integrations/gas/connection', {
      execUrl,
      // Omit token on an edit → backend keeps the already-saved secret.
      ...(token ? { token } : {}),
      ...(displayName ? { displayName } : {}),
    });
  }

  /**
   * Reveal the saved token (decrypted) for pre-filling the Edit form.
   * Backend gates this on `inbound.manage`, so only users who can edit the
   * connection can read the secret.
   */
  async revealGasToken(): Promise<{ token: string | null }> {
    return this.get<{ token: string | null }>(
      '/integrations/gas/connection/token',
    )
  }

  /** Remove the org's connection. */
  async deleteGasConnection(): Promise<{ deleted: true }> {
    return this.delete<{ deleted: true }>('/integrations/gas/connection');
  }

  /** List the connected sheet's tab names. */
  async getGasTabs(): Promise<{ tabs: string[] }> {
    return this.get<{ tabs: string[] }>('/integrations/gas/tabs');
  }

  /** Read + parse a tab into inbound boxes. */
  async parseGasSheet(tab: string): Promise<GasParseResult> {
    return this.post<GasParseResult>('/integrations/gas/parse', { tab });
  }

  /** Create (or dry-run) inbound orders from selected GAS boxes. */
  async importGasSheet(payload: GasImportPayload): Promise<GasImportResponse> {
    return this.post<GasImportResponse>('/integrations/gas/import', payload);
  }

  // --- Uploaded packing list (.xlsx read in the browser) --------------------
  // The workbook is parsed client-side into a grid of cells; the server still
  // decides how rows become boxes, so both import routes share one parser.

  async parsePackingListRows(
    payload: PackingListParsePayload,
  ): Promise<PackingListParseResult> {
    return this.post<PackingListParseResult>(
      '/inbound-orders/packing-list/parse',
      payload,
    );
  }

  async importPackingList(
    payload: PackingListImportPayload,
  ): Promise<InboundImportResponse> {
    return this.post<InboundImportResponse>(
      '/inbound-orders/packing-list/import',
      payload,
    );
  }
}

// ============================================================================
// PARSED PACKING-LIST TYPES (shared by the Apps Script import)
// ============================================================================

/** A single catalog line within a parsed box. */
/** Filter value for "no category set". Same sentinel as the backend. */
export const UNCLASSIFIED_CATEGORY = '__none__';

export type StockDetailStatus = 'available' | 'loose' | 'boxed' | 'received' | 'allocated';
export type StockDetailExpiry =
  | 'expired'
  | 'thisMonth'
  | 'within30'
  | 'within90'
  | 'notExpired'
  | 'noExpiry';

export interface StockDetailParams {
  warehouseId?: string;
  catalogNumber?: string;
  name?: string;
  /** One category, or UNCLASSIFIED_CATEGORY. */
  category?: string;
  /** A chosen warehouse or location id: that place and everything under it. */
  locationId?: string;
  stockStatus?: StockDetailStatus;
  expiry?: StockDetailExpiry;
  includeZeroStock?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface StockDetailRow {
  productId: string;
  catalogNumber: string;
  name: string;
  category: string | null;
  baseUom: string | null;
  warehouseId: string | null;
  warehouseCode: string | null;
  /** null = not put away yet (or a product with no stock). */
  locationId: string | null;
  location: {
    zone: string | null;
    aisle: string | null;
    rack: string | null;
    level: string | null;
    bin: string | null;
  } | null;
  batchId: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  totalQty: number;
  /** AVAILABLE + sealed-box STORED (boxed stock is sellable). */
  availableQty: number;
  /** The sealed-box slice of availableQty. */
  boxedQty: number;
  boxCount: number;
  receivedQty: number;
  allocatedQty: number;
}

export interface StockDetailPage<T> {
  items: T[];
  total: number;
  totalUnits: number;
  limit: number;
  offset: number;
}

export type StockDetailResponse = StockDetailPage<StockDetailRow>;

/** One product summed over every location and batch of the filtered stock. */
export interface StockDetailProductRow {
  productId: string;
  catalogNumber: string;
  name: string;
  category: string | null;
  baseUom: string | null;
  /** How many locations hold it (units not yet put away hold none). */
  locationCount: number;
  totalQty: number;
  availableQty: number;
  boxedQty: number;
  boxCount: number;
  receivedQty: number;
  allocatedQty: number;
  earliestExpiry: string | null;
}

export interface StockLocationOptions {
  warehouses: { id: string; code: string; name: string; facilityId: string }[];
  locations: {
    id: string;
    warehouseId: string;
    locationType: string | null;
    name: string | null;
    zone: string | null;
    aisle: string | null;
    rack: string | null;
    level: string | null;
    bin: string | null;
  }[];
}

function stockDetailQuery(params: StockDetailParams): string {
  const qs = new URLSearchParams();
  const put = (key: string, value: string | number | undefined | null) => {
    if (value !== undefined && value !== null && value !== '') qs.append(key, String(value));
  };
  put('warehouseId', params.warehouseId);
  put('catalogNumber', params.catalogNumber?.trim());
  put('name', params.name?.trim());
  put('category', params.category);
  put('locationId', params.locationId);
  put('stockStatus', params.stockStatus);
  put('expiry', params.expiry);
  if (params.includeZeroStock) qs.append('includeZeroStock', 'true');
  put('sortBy', params.sortBy);
  put('sortOrder', params.sortOrder);
  put('limit', params.limit);
  put('offset', params.offset);
  return qs.toString();
}

export interface GoogleSheetParsedItem {
  catalogNumber: string;
  quantityExpected: number;
  description?: string;
  batchNo?: string;
  expiryDate?: string;
}

/** A group of items sharing one box label, with 1-indexed sheet row span. */
export interface GoogleSheetParsedBox {
  boxLabel: string;
  startRow: number;
  endRow: number;
  items: GoogleSheetParsedItem[];
  /** Auto-derived reference (e.g. `BOX#3-393`); prefill for the editable field. */
  referenceNumber?: string;
}

/** One order to import — one box maps to one order. */
export interface InboundImportOrder {
  inboundType: 'PRODUCTION';
  notes: string;
  referenceNumber?: string;
  items: GoogleSheetParsedItem[];
}

export interface InboundImportPayload {
  warehouseCode?: string;
  dryRun: boolean;
  orders: InboundImportOrder[];
}

/**
 * Why a box was not created. The server's `error` is English and technical;
 * this code is what the UI translates, so a rejection reads in the operator's
 * own language without matching on the message text.
 */
export type InboundImportReasonCode =
  | 'DUPLICATE_REFERENCE_IN_BATCH'
  /**
   * Nothing was wrong with this box. The import was refused because other boxes
   * in the same batch share a reference, and a batch is all or nothing.
   */
  | 'BATCH_REJECTED_DUPLICATE_REFERENCES'
  | 'REFERENCE_ALREADY_IMPORTED'
  | 'UNKNOWN_CATALOG_NUMBERS'
  | 'SUPPLIER_NOT_FOUND'
  /** A line's product needs a batch number / expiry date and the row has none. */
  | 'BATCH_REQUIRED'
  | 'EXPIRY_REQUIRED';

export interface InboundImportResult {
  index: number;
  referenceNumber: string | null;
  success: boolean;
  skipped: boolean;
  /** The order this box became — or, when skipped, the one it collided with. */
  orderId?: string;
  orderNumber?: string;
  error: string | null;
  /** Set on every skip and every failure; absent on success. */
  reasonCode?: InboundImportReasonCode;
  resolvedCatalogs: string[];
  skippedCatalogs: string[];
  autoCreatedProducts: string[];
}

export interface InboundImportSummary {
  total: number;
  created: number;
  skipped: number;
  failed: number;
  autoCreated: number;
  /**
   * The whole batch was refused and NOTHING was written, because two or more
   * boxes carried the same reference. Distinguishes a refusal from an import
   * that merely happened to create nothing.
   */
  batchRejected?: boolean;
  /** The references that clash, so the UI can name them. */
  duplicateReferences?: string[];
}

export interface InboundImportResponse {
  results: InboundImportResult[];
  summary: InboundImportSummary;
}

// ============================================================================
// GAS WEB APP INTEGRATION TYPES (inbound import via Apps Script /exec URL)
// ============================================================================

export interface GasConnectionStatus {
  connected: boolean;
  execUrl?: string;
  displayName?: string;
}

export interface GasParseResult {
  /** Present when the requested tab was not found (tab discovery). */
  tabs?: string[];
  tab?: string;
  headerRow?: number;
  boxes?: GoogleSheetParsedBox[];
}

/**
 * A grid of cells read from an uploaded workbook. Never contains Date objects —
 * date cells travel as raw Excel serials so JSON can't shift the calendar day.
 */
export type SheetCellValue = string | number | null;

export interface PackingListParsePayload {
  /** File base name; the box reference is derived from the digits in it. */
  sourceName?: string;
  rows: SheetCellValue[][];
}

export interface PackingListParseResult {
  headerRow: number;
  boxes: GoogleSheetParsedBox[];
}

export interface PackingListImportPayload {
  sourceName?: string;
  dryRun: boolean;
  warehouseCode?: string;
  /** Items are required — an uploaded file cannot be re-read server-side. */
  boxes: Array<{
    boxLabel: string;
    referenceNumber?: string;
    printChoice?: string;
    items: Array<{
      catalogNumber: string;
      quantityExpected: number;
      batchNo?: string;
      expiryDate?: string;
      description?: string;
    }>;
  }>;
}

export interface GasImportPayload {
  tab: string;
  dryRun: boolean;
  warehouseCode?: string;
  boxes: Array<{
    boxLabel: string;
    referenceNumber?: string;
    printChoice?: 'ITEMS_ONLY' | 'BOX_ONLY' | 'BOX_AND_ITEMS';
    items?: Array<{
      catalogNumber: string;
      quantityExpected: number;
      batchNo?: string;
      expiryDate?: string;
    }>;
  }>;
}

export interface GasImportResponse {
  results: InboundImportResult[];
  summary: InboundImportSummary;
  /** Set when orders were created but highlighting the sheet failed. */
  writeBackWarning?: string;
}

// ============================================================================
// PRINT QUEUE TYPES (Exported for reuse across components)
// Maps to: print_jobs table in PostgreSQL
// ============================================================================

// A put-away box's open-state (derived backend-side from member print_status).
export type StoredBoxOpenState = 'SEALED' | 'OPENING' | 'VERIFIED' | 'LABELED';

export interface StoredBox {
  containerId: string;
  lpnCode: string;
  locationId: string | null;
  locationName: string | null;
  composition: string | null;
  itemCount: number;
  skuCount: number;
  openState: StoredBoxOpenState;
  /** The box's own lifecycle state; DECONSOLIDATING = an unbox is in progress. */
  state: string;
  /** Who started the unbox and when (set while DECONSOLIDATING). */
  openedAt: string | null;
  openedByName: string | null;
}

export interface CancelBoxOpenResult {
  containerId: string;
  lpnCode: string;
  state: 'SEALED';
  labelsWithdrawn: number;
  itemsRelabeled: number;
  printJobsCancelled: number;
}

export interface PutbackItem {
  id: string;
  epc: string | null;
  serialNumber: string | null;
  catalogNumber: string | null;
  productId: string;
  locationId: string | null;
  locationName: string | null;
  warehouseId: string | null;
  lastMovedAt: string | null;
}

// ── Putback v2 (read-only oversight) ──────────────────────────────────────────

export type PutbackUnitStatus = 'PENDING' | 'COMPLETED';

// Level 1 — one row per cancellation episode.
export interface PutbackOutbound {
  headerId: string;
  outboundOrderId: string | null;
  orderNumber: string | null;
  referenceNumber: string | null;
  episodeNo: number | null;
  createdAt: string | null;
  totalUnits: number;
  completedUnits: number;
}

export interface PutbackOutboundsResponse {
  outbounds: PutbackOutbound[];
  total: number;
}

// Level 2 — item lines within an episode.
export interface PutbackLineSummary {
  lineId: string;
  productId: string | null;
  catalogNumber: string | null;
  sourceLocationName: string | null;
  batchNo: string | null;
  totalUnits: number;
  completedUnits: number;
}

export interface PutbackLinesResponse {
  orderNumber: string | null;
  episodeNo: number | null;
  lines: PutbackLineSummary[];
}

// Level 3 — units within a line.
export interface PutbackUnit {
  id: string;
  epc: string | null;
  serialNumber: string | null;
  status: PutbackUnitStatus;
  putbackLocationName: string | null;
  completedAt: string | null;
}

export interface PutbackLineDetail {
  lineId: string;
  catalogNumber: string | null;
  sourceLocationName: string | null;
  requiredQty: number;
  completedQty: number;
  remainingQty: number;
  units: PutbackUnit[];
}

export type PrintJobType = 'PRODUCTION_LABEL' | 'INBOUND_LABEL' | 'LOCATION_LABEL' | 'CONTAINER_LABEL' | 'SHIPPING_LABEL';
export type PrintJobStatus = 'PENDING' | 'PRINTING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'EXPIRED';
export type PrintJobPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type LabelFormat = 'ZPL' | 'EPL' | 'PDF' | 'PNG';

export interface DisposableItemLocation {
  zone: string | null;
  aisle: string | null;
  rack: string | null;
  level: string | null;
  bin: string | null;
}

export interface DisposableItem {
  id: string
  epc: string | null
  serialNumber: string | null
  status: string
  location: DisposableItemLocation | null
  containerId?: string | null
  containerLpn?: string | null
  containerState?: string | null
  /** false = a boxed item that can't be pulled out on its own right now (open the box, or dispose the whole box) */
  boxDisposable?: boolean
}

export interface DisposableBatch {
  batchId: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  itemCount: number;
  items: DisposableItem[];
}

export interface DisposableProduct {
  productId: string | null;
  catalogNumber: string | null;
  productName: string | null;
  totalItemCount: number;
  batches: DisposableBatch[];
}

export interface DisposableBoxItem {
  id: string
  epc: string | null
  serialNumber: string | null
  status: string
  productId: string | null
  catalogNumber: string | null
  productName: string | null
  batchId: string | null
  batchNo: string | null
  expiryDate: string | null
  containerLpn: string
}

export interface DisposableBox {
  id: string
  lpnCode: string
  epc: string | null
  state: string
  type: string | null
  location: (DisposableItemLocation & { name?: string | null }) | null
  memberCount: number
  disposableCount: number
  /** true when every live member can go — ticking the box scraps it whole */
  wholeBox: boolean
  blocker: 'DECONSOLIDATING' | 'STORING' | null
  /** Who is mid-open on the box (set while DECONSOLIDATING). */
  openedAt: string | null
  openedByName: string | null
  contents: { catalogNumber: string | null; productName: string | null; batchNo: string | null; expiryDate: string | null; count: number }[]
  items: DisposableBoxItem[]
}

export interface PrintQueueStats {
  pending: number;
  printing: number;
  completed: number;
  failed: number;
  cancelled: number;
  expired: number;
  totalToday: number;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
}

export interface PrintJob {
  id: string;
  organizationId: string;
  facilityId: string | null;
  facility?: { id: string; name: string; code: string } | null;
  warehouseId: string | null;
  warehouse?: { id: string; name: string; code: string } | null;
  type: PrintJobType;
  status: PrintJobStatus;
  priority: PrintJobPriority;
  priorityOrder: number;
  batchId: string | null;
  batchSequence: number | null;
  title: string;
  description: string | null;
  notes: string | null;
  referenceId: string | null;
  referenceType: string | null;
  referenceNumber: string | null;
  // Inbound order this job belongs to (item label via referenceId, box label
  // via jobData.inboundOrderId) — used to group the print queue by order.
  inboundOrderNumber: string | null;
  inboundOrderId: string | null;
  // The inbound order's customer reference (for box labels; referenceNumber shows
  // the LPN for container jobs).
  inboundReferenceNumber: string | null;
  totalLabels: number;
  copiesPerLabel: number;
  printedLabels: number;
  failedLabels: number;
  printerId: string | null;
  printerAddress: string | null;
  printerName: string | null;
  printerPort: number;
  templateId: string | null;
  labelWidthMm: number | null;
  labelHeightMm: number | null;
  labelFormat: LabelFormat;
  jobData: Record<string, any>;
  errorMessage: string | null;
  errorCode: string | null;
  lastErrorAt: string | null;
  retryCount: number;
  maxRetries: number;
  retryDelaySeconds: number;
  nextRetryAt: string | null;
  createdBy: string;
  creator?: { id: string; fullName: string; email: string } | null;
  processedBy: string | null;
  processor?: { id: string; fullName: string; email: string } | null;
  cancelledBy: string | null;
  canceller?: { id: string; fullName: string; email: string } | null;
  cancellationReason: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface PrintJobFilters {
  facilityId?: string;
  warehouseId?: string;
  type?: string;
  status?: string;
  priority?: string;
  batchId?: string;
  referenceId?: string;
  referenceType?: string;
  search?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreatePrintJobDto {
  type: PrintJobType;
  title: string;
  description?: string;
  notes?: string;
  facilityId?: string;
  warehouseId?: string;
  referenceId?: string;
  referenceType?: string;
  totalLabels?: number;
  copiesPerLabel?: number;
  printerId?: string;
  printerAddress?: string;
  printerName?: string;
  printerPort?: number;
  templateId?: string;
  labelWidthMm?: number;
  labelHeightMm?: number;
  labelFormat?: LabelFormat;
  priority?: PrintJobPriority;
  jobData?: Record<string, any>;
  scheduledAt?: string;
  expiresAt?: string;
  maxRetries?: number;
  retryDelaySeconds?: number;
}

// Product Import Types
export interface ProductImportFieldOption {
  value: string;
  label: string;
  aliases: string[];
}

export interface ProductImportFieldConfig {
  field: string;
  label: string;
  required: boolean;
  type: 'string' | 'number' | 'boolean' | 'enum';
  options?: ProductImportFieldOption[];
  aliases: string[];
  hint?: string;
}

export interface ProductImportParseResult {
  fileId: string;
  headers: string[];
  sampleRows: Record<string, string>[];
  totalRows: number;
  suggestedMappings: Record<string, string>;
}

export interface ProductImportValidationError {
  field: string;
  message: string;
}

export interface ProductImportFieldChange {
  field: string;
  label: string;
  from: string;
  to: string;
}

export interface ProductImportValidatedRow {
  rowNumber: number;
  data: Record<string, any>;
  // 'update' edits the existing product with this catalog number.
  action?: 'create' | 'update';
  productId?: string;
  changes?: ProductImportFieldChange[];
}

export interface ProductImportInvalidRow {
  rowNumber: number;
  data: Record<string, string>;
  errors: ProductImportValidationError[];
}

export interface ProductImportSkippedRow {
  rowNumber: number;
  catalogNumber: string;
  reason: string;
}

export interface ProductImportValidationResult {
  valid: ProductImportValidatedRow[];
  invalid: ProductImportInvalidRow[];
  skipped: ProductImportSkippedRow[];
  summary: {
    total: number;
    valid: number;
    invalid: number;
    skipped: number;
    // Existing products the file leaves as they are: counted, not listed.
    unchanged: number;
    creates: number;
    updates: number;
  };
}

export interface ProductImportResult {
  imported: number;
  updated: number;
  skipped: number;
  unchanged: number;
  errors: string[];
}

// NFC name card: a tap types displayName into "marked by" fields.
// Never linked to a user; uidSuffix is the only part of the UID exposed.
export interface NfcCard {
  id: string;
  displayName: string;
  paired: boolean;
  uidSuffix: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface ProductImportTemplate {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  columnMappings: Record<string, string>;
  defaultValues?: Record<string, any>;
  valueTransformations?: Record<string, Record<string, string>>;
  isDefault: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

// Stock Take type (uses stock_takes table)
// Schema: id, organization_id, warehouse_id, reference_no, status,
//         target_location_id, assigned_to_user_id, started_at, completed_at, created_at
interface StockTakeTask {
  id: string;
  referenceNo: string;
  status: 'PLANNED' | 'ASSIGNED' | 'IN_PROGRESS' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
  targetLocation?: { id: string; zone?: string | null; aisle?: string | null; rack?: string | null; level?: string | null; bin?: string | null } | null;
  warehouse?: { id: string; name: string; code: string } | null;
  assignedToUser?: { id: string; fullName: string; email: string } | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  reference?: string | null;
  note?: string | null;
  // Computed counts
  countedItemCount?: number;
  expectedItemCount?: number;
  // Items and sealed box labels apart (see the backend's countItemsAndLabels).
  expectedItems?: number;
  foundItems?: number;
  expectedLabels?: number;
  foundLabels?: number;
  scans?: any[];
}

// Stock take sessions: many handhelds on one count, each scanning in a numbered session.
export interface StockTakePerson {
  id: string;
  name: string;
}

/** Labels = items (own label) + sealed boxes (one label each); each unit once. */
export interface StockTakeCountTotals {
  itemsExpected: number;
  itemsFound: number;
  boxesExpected: number;
  boxesFound: number;
  labelsExpected: number;
  labelsFound: number;
  /** What the expected sealed boxes hold: said, never counted as labels. */
  itemsInBoxes: number;
}

/** NOT_SAVED: the count closed before that session was saved. */
export type StockTakeSessionStatus = 'SCANNING' | 'SAVED' | 'NOT_SAVED';

/** Per-session numbers overlap: two sessions reading one unit both keep it. */
export interface StockTakeSessionRow {
  number: number;
  clientSessionId: string;
  startedBy: StockTakePerson | null;
  startedAt: string;
  savedAt: string | null;
  startNote: string | null;
  finishNote: string | null;
  status: StockTakeSessionStatus;
  itemsRead: number;
  boxesRead: number;
  labelsRead: number;
}

export interface StockTakeOverview {
  id: string;
  referenceNo: string;
  status: StockTakeTask['status'];
  warehouseId: string;
  targetLocationId: string | null;
  locationCode: string | null;
  startedBy: StockTakePerson | null;
  startedAt: string | null;
  finishedBy: StockTakePerson | null;
  finishedAt: string | null;
  cancelledBy: StockTakePerson | null;
  cancelledAt: string | null;
  totals: StockTakeCountTotals;
  /** Newest first; empty for a count from before sessions. */
  sessions: StockTakeSessionRow[];
}

/** A session that read a unit or box, and when. */
export interface StockTakeSessionReader {
  number: number;
  by: StockTakePerson | null;
  at: string;
}

export interface StockTakeSessionRead {
  readAt: string;
  kind: 'ITEM' | 'BOX' | 'BOX_OPENED';
  /** EPC / serial for an item, LPN for a box label. */
  code: string;
  catalogNumber: string | null;
  productName: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  /** A box label: what the box held when the count began. */
  contents: Array<{
    catalogNumber: string | null;
    productName: string | null;
    batchNo: string | null;
    expiryDate: string | null;
    qty: number;
  }>;
  /** Where the operator was counting. */
  locationCode: string | null;
  /** The other sessions that read the same unit or box. */
  alsoReadIn: StockTakeSessionReader[];
}

// Expiry Monitor Types
export interface ExpirySummary {
  expired: number;
  total: number;
}

export interface ExpiryBatchItem {
  batchId: string;
  /** Null for a numberless batch (the product's batch is optional). */
  batchNumber: string | null;
  /** A batch without an expiry never expires; the page leaves it out. */
  expiryDate: string | null;
  daysRemaining: number | null;
  productId: string;
  productName: string;
  catalogNumber: string | null;
  itemCount: number;
  locationId: string | null;
  zone: string | null;
  aisle: string | null;
  rack: string | null;
  level: string | null;
  bin: string | null;
}

// Report Types
export interface ReportInventoryItem {
  productId: string;
  catalogNumber: string;
  name: string;
  category: string | null;
  totalOnHand: number;
  received: number;
  available: number;
  /** Sealed-box (STORED) units — nominal/unverified until opened. Kept separate from `available`. */
  boxed: number;
  /** Picked-then-cancelled units awaiting reshelve (PUTBACK_PENDING). On hand, not sellable. */
  putbackPending: number;
  allocated: number;
  agingBrackets: {
    days0to30: number;
    days31to60: number;
    days61to90: number;
    days90plus: number;
  };
  lowStockAlert: boolean;
  reorderPoint: number;
}

export interface ReportMovementItem {
  transactionType: string;
  timestamp: string;
  catalogNumber: string | null;
  productName: string | null;
  quantityChange: string;
  fromLocationId: string | null;
  toLocationId: string | null;
  userName: string | null;
  notes: string | null;
  /** Why it moved, as the operator gave it: the picked reason (description, else
   *  code) and a disposal's typed note, joined with " — ". Null when none. */
  reason: string | null;
}

export type StockCardJobStatus = 'QUEUED' | 'PROCESSING' | 'DONE' | 'FAILED';

export interface StockCardJob {
  id: string;
  warehouseId: string | null;
  status: StockCardJobStatus;
  dateFrom: string | null;
  dateTo: string | null;
  fileName: string | null;
  rowCount: number | null;
  fileSize: number | null;
  errorMessage: string | null;
  createdBy: string;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface StockCardSummaryRow {
  catalogNumber: string;
  productName: string;
  opening: number;           // physical on hand at window start (internal math)
  openingAvailable: number;  // on shelf AND not committed to an order, at start
  openingPending: number;    // in the receiving dock at window start
  openingAllocated: number;  // committed but still on premises at start (excluded from total)
  openingTotal: number;      // = openingAvailable + openingPending
  in: number;                // became available during window (completed putaways)
  found: number;             // recoveries during window
  out: number;               // physical departures during window, deduped (closing math)
  allocated: number;         // items allocated (committed/picked) during window
  shipped: number;           // items physically shipped (dispatched) during window
  disposed: number;          // disposed during window
  returned: number;          // came back via a RETURN order during window
  lost: number;              // flagged missing during window
  pendingIn: number;         // NEW arrivals still in the dock at window END
  closing: number;           // physical on hand at window end (internal math)
  closingAvailable: number;  // on shelf AND not committed, at end
  closingPending: number;    // in the receiving dock at window end
  closingAllocated: number;  // committed but still on premises at end
  closingTotal: number;      // = closingAvailable + closingPending
  openingPutback: number;    // picked-then-cancelled, awaiting reshelve, at window start (excluded from total)
  windowPutbackNew: number;  // entered putback-pending during the window
  windowPutbackDone: number; // reshelved during the window (raises Closing Available)
  closingPutback: number;    // picked-then-cancelled, awaiting reshelve, at window end (excluded from total)
  openingBoxed: number;      // units sealed in a box at window start (excluded from total until unboxed)
  windowBoxedNew: number;    // units boxed during the window
  windowUnboxed: number;     // units taken out of a box during the window (raises Closing Available / the Total)
  closingBoxed: number;      // units sealed in a box at window end (excluded from total until unboxed)
}

export type StockCardDirection = 'PENDING_IN' | 'IN' | 'PENDING_OUT' | 'OUT';

export interface StockCardEventRow {
  timestamp: string;
  direction: StockCardDirection;
  subcategory: string;
  referenceType: string | null;
  referenceId: string | null;
  referenceNumber: string | null;     // internal order # (OUT-XXX / INB-XXX)
  externalReference: string | null;   // customer's PO / external ref (Shipment + Allocation)
  partyName: string | null;
  /** Disposal lines: the reason picked (description, else code) + the note typed,
   *  joined with " — ". Shown in the Customer / Supplier column. */
  reason: string | null;
  epc: string | null;
  fromLocation: string | null;
  toLocation: string | null;
  qtyIn: number;
  qtyOut: number;
  balance: number;
}

export interface ReportStockTakeItem {
  id: string;
  referenceNo: string;
  reference: string | null;
  status?: 'COMPLETED' | 'REVIEW' | 'IN_PROGRESS' | 'ASSIGNED' | 'PLANNED' | 'CANCELLED';
  finishedAt?: string | null;
  completedAt: string;
  createdAt: string;
  warehouseName: string | null;
  // Target location fields — flat so they survive the SQL group-by. Mirror
  // the SimpleLocationData shape consumed by `formatLocation` in the UI.
  targetLocationId: string | null;
  targetLocationZone: string | null;
  targetLocationAisle: string | null;
  targetLocationRack: string | null;
  targetLocationLevel: string | null;
  targetLocationBin: string | null;
  // Operator = the user the take was assigned to (the person who did the
  // scanning). Falls back to username if fullName is null.
  operatorName: string | null;
  operatorUsername: string | null;
  totalExpected: number;
  totalCounted: number;
  totalVariance: number;
  // Items and sealed box labels apart, as the count's page and the handheld show them
  // (absent on older backends).
  expectedItems?: number;
  foundItems?: number;
  expectedLabels?: number;
  foundLabels?: number;
  /** What the sealed boxes held, and of that what was counted by its box label. */
  itemsInBoxes?: number;
  foundInBoxes?: number;
  // List-view-only fields powering the combined-report multi-select rules.
  // Absent on `export: true` responses (see ReportsService.getStockTakeReport).
  warehouseId?: string;
  locationIds?: string[];
}

export interface ReportStockTakeData {
  items: ReportStockTakeItem[];
  total: number;
  summary: {
    totalStockTakes: number;
    itemsWithVariance: number;
    netVariance: number;
  };
}

export interface ReportInboundItem {
  id: string;
  orderNumber: string;
  referenceNumber: string | null;
  status: string;
  inboundType: string;
  supplierName: string | null;
  warehouseName: string | null;
  expectedArrivalDate: string | null;
  actualArrivalDate: string | null;
  createdAt: string;
  totalExpected: number;
  totalReceived: number;
  lineCount: number;
}

export interface ReportInboundData {
  items: ReportInboundItem[];
  total: number;
  summary: {
    total: number;
    byStatus: Record<string, number>;
  };
}

export interface ReportOutboundItem {
  id: string;
  orderNumber: string;
  referenceNumber: string | null;
  status: string;
  customerName: string | null;
  warehouseName: string | null;
  carrierName: string | null;
  trackingNumber: string | null;
  requestedDeliveryDate: string | null;
  createdAt: string;
  totalOrdered: number;
  totalPicked: number;
  lineCount: number;
}

export interface ReportOutboundData {
  items: ReportOutboundItem[];
  total: number;
  summary: {
    total: number;
    byStatus: Record<string, number>;
  };
}

// Line-item types for detailed report exports
export interface InventoryLineItem {
  productId: string;
  epc: string;
  serialNumber: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  locationName: string;
  status: string;
  quantity: string;
}

export interface InboundLineItem {
  inboundOrderId: string;
  catalogNumber: string | null;
  productName: string | null;
  uom: string;
  quantityExpected: number;
  quantityReceived: number;
  batchNo: string | null;
  expiryDate: string | null;
}

export interface OutboundLineItem {
  outboundOrderId: string;
  catalogNumber: string | null;
  productName: string | null;
  uom: string;
  quantityOrdered: number;
  quantityPicked: number;
}

export interface StockTakeLineItem {
  stockTakeId: string;
  catalogNumber: string | null;
  productName: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  systemQty: number;
  countedQty: number;
  variance: number;
  /** The line's items, counted by their own tag (sealed box contents are box labels). */
  expectedItems?: number;
  foundItems?: number;
  /** What sealed boxes hold of this line, and of that what was counted by its box label. */
  inBoxes?: number;
  inBoxesFound?: number;
}

/**
 * One row per inventory_item that's currently flagged MISSING but was
 * scanned in at least one of the combined-report's constituent takes.
 * Surfaced in the combined PDF's "Found in scan but still MISSING" review
 * section for manual operator follow-up via /inventory.
 */
export interface CombinedReviewItem {
  inventoryItemId: string;
  epc: string;
  catalogNumber: string | null;
  productName: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  /** Reference numbers of the selected takes that scanned this item. */
  scannedInStockTakeRefs: string[];
  /** Reference number of the take (anywhere — not necessarily in the
   *  selection) that first flagged this item MISSING. */
  firstFlaggedStockTakeRef: string | null;
  /** ISO timestamp when the item was first flagged MISSING. */
  missingSince: string | null;
}

/**
 * One row per inventory_item that either moved INTO the target location
 * between the earliest and latest take in the combined selection, or
 * moved OUT during that window. Powers the "Stock Movement During
 * Combined Window" section in the combined PDF — explains the Expected
 * delta between the union of all takes and any single take's count.
 */
export interface CombinedMovementItem {
  inventoryItemId: string;
  epc: string | null;
  status: string | null;
  catalogNumber: string | null;
  productName: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  currentLocationZone: string | null;
  currentLocationAisle: string | null;
  currentLocationRack: string | null;
  currentLocationLevel: string | null;
  currentLocationBin: string | null;
  outboundOrderNumbers: string[];
  inboundOrderNumbers: string[];
}

export interface CombinedMovementResponse {
  earliestTake: { id: string; referenceNo: string; createdAt: string };
  latestTake: { id: string; referenceNo: string; createdAt: string };
  movedOut: CombinedMovementItem[];
  movedIn: CombinedMovementItem[];
  netChange: number;
}

/**
 * Per-EPC row for the combined PDF's Missing Items Detail section: items
 * expected by at least one selected take but scanned in none. Includes
 * the take refs that expected the item so the operator can see the
 * source.
 */
export interface CombinedMissingDetailItem {
  inventoryItemId: string;
  epc: string | null;
  status: string | null;
  catalogNumber: string | null;
  productName: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  currentLocationZone: string | null;
  currentLocationAisle: string | null;
  currentLocationRack: string | null;
  currentLocationLevel: string | null;
  currentLocationBin: string | null;
  expectedInStockTakeRefs: string[];
  outboundOrderNumbers: string[];
}

export interface CombinedPerCatalogCount {
  catalogNumber: string;
  productName: string | null;
  expectedCount: number;
  scannedCount: number;
  /** Items only (counted by their own tag); a sealed box's units are its box label. */
  expectedItems?: number;
  foundItems?: number;
}

export interface CombinedPerGroupCount {
  catalogNumber: string;
  productName: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  expectedCount: number;
  scannedCount: number;
  /** Items only (counted by their own tag); a sealed box's units are its box label. */
  expectedItems?: number;
  foundItems?: number;
}

/** A sealed box of a combined selection: one box label, found when it was read. */
export interface CombinedBoxLabel {
  containerId: string;
  lpnCode: string;
  state: string;
  itemCount: number;
  skuCount: number;
  read: boolean;
  location: {
    zone: string | null;
    aisle: string | null;
    rack: string | null;
    level: string | null;
    bin: string | null;
  } | null;
}

export interface CombinedMissingDetailResponse {
  missing: CombinedMissingDetailItem[];
  accountedElsewhere: CombinedMissingDetailItem[];
  expectedUnionCount: number;
  scannedUnionCount: number;
  /** Items and box labels apart (absent on older backends). */
  expectedItems?: number;
  foundItems?: number;
  expectedLabels?: number;
  foundLabels?: number;
  boxes?: CombinedBoxLabel[];
  perCatalog: CombinedPerCatalogCount[];
  perGroup: CombinedPerGroupCount[];
}

export interface CombinedInboundItem {
  inventoryItemId: string;
  epc: string | null;
  catalogNumber: string | null;
  productName: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  orderNumber: string;
  completedAt: string | null;
  supplierName: string | null;
}

export interface CombinedOutboundItem {
  inventoryItemId: string;
  epc: string | null;
  catalogNumber: string | null;
  productName: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  orderNumber: string;
  shippedAt: string | null;
  customerName: string | null;
}

export interface CombinedInboundOutboundResponse {
  window: { start: string | null; end: string | null };
  inbounded: CombinedInboundItem[];
  outbounded: CombinedOutboundItem[];
}

export const api = new ApiClient(API_BASE_URL);

// Dispatch master data (see /settings/dispatch)
export interface Driver {
  id: string;
  name: string;
  phone: string | null;
  // The usual vehicle: a handheld picker default, never a constraint.
  defaultVehicleId: string | null;
  defaultVehiclePlate: string | null;
  // The colleague who usually rides along — the second handheld default.
  defaultAssistantDriverId: string | null;
  defaultAssistantDriverName: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Vehicle {
  id: string;
  plate: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}
